import {
  CLCustomButton,
  CLPrimaryCustomButton,
  CLPrimaryCustomTextarea
} from '@/components/ui-controls';
import CLButton from '@/components/ui-controls/default-ui-controls/button';
import CLTooltip from '@/components/utils/tooltip';
import CommonConstants from '@/constants/common-constants';
import {
  DELETE_RUN_CHECKLIST_COMMENT_BY_ID_MUTATION,
  GET_RUN_CHECKLIST_COMMENTS_BY_CHECKLIST_ID_QUERY,
  GET_RUN_CHECKLIST_COMMENTS_BY_TASK_ID_QUERY,
  INSERT_RUN_CHECKLIST_COMMENT_MUTATION,
  UPDATE_RUN_CHECKLIST_COMMENT_MUTATION
} from '@/graphql/checklist-comments.gql';
import { downloadImage } from '@/services/axios/dowload-file';
import { convertISOToLocalDateString } from '@/services/utils/utils.service';
import { useOrganizationStore } from '@/stores/organization.store';
import { useRunChecklistStore } from '@/stores/run/run-checklist.store';
import { OrgFile } from '@/types/chcklist-component.type';
import {
  RunChecklistComment,
  RunChecklistCommentFile,
  RunChecklistComments
} from '@/types/run-checklist-comments.type';
import { RunChecklist } from '@/types/run.type';
import { User } from '@/types/user.type';
import { useMutation, useQuery } from '@apollo/client';
import { Dialog, Transition } from '@headlessui/react';
import { useFileUpload, useNhostClient, useUserData } from '@nhost/nextjs';
import React, {
  Fragment,
  KeyboardEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { shallow } from 'zustand/shallow';
import { RunChecklistTree } from '../run-by-id.component';

interface RunCommentsComponentProps {
  runChecklistId: string;
}

interface uploadedFileProps {
  file: File;
  previewUrl?: string;
  publicUrl?: string;
}

interface CommentRowProps {
  comment: RunChecklistComment;
  onDelete: (c: RunChecklistComment) => void;
  isSingleResponse: boolean;
  runChecklistTree: RunChecklistTree; // you’ll need to pass this in
  isEditing: boolean;
  index: number;
  isReplying: boolean;
}

export default function RunCommentsComponent({
  runChecklistId
}: RunCommentsComponentProps) {
  // Variables
  const nhost = useNhostClient();
  const { upload } = useFileUpload();
  const userData = useUserData();

  // States
  const [runChecklistComments, setRunChecklistComments] =
    useState<RunChecklistComment[]>();
  const [runChecklistReplyComments, setRunChecklistReplyComments] =
    useState<RunChecklistComment[]>();
  const [
    runChecklistReplyToReplyComments,
    setRunChecklistReplyToReplyComments
  ] = useState<RunChecklistComment[]>();
  const [runChecklistTaskReplyComments, setRunChecklistTaskReplyComments] =
    useState<RunChecklistComment[]>();
  const [
    runChecklistTaskReplyToReplyComments,
    setRunChecklistTaskReplyToReplyComments
  ] = useState<RunChecklistComment[]>();
  const [runTaskComments, setRunTaskComments] =
    useState<RunChecklistComment[]>();
  const [showCommentText, setShowCommentText] = useState<boolean>(false);
  const [commentText, setCommentText] = useState<string>('');
  const [previewText, setPreviewText] = useState<string>('');
  const [mentionPrefix, setMentionPrefix] = useState('');
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionResults, setMentionResults] = useState<typeof allTasks>([]);
  const [selectedMentionTaskId, setSelectedMentionTaskId] = useState<
    string | null
  >(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [uploadedFiles, setUploadedFiles] = useState<uploadedFileProps[]>([]);
  const [isCommentSaving, setIsCommentSaving] = useState<boolean>(false);

  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [previewEditText, setPreviewEditText] = useState<string>('');

  // hold the ID of the comment that’s currently replying (or null if none)
  const [replyingCommentId, setReplyingCommentId] = useState<string | null>(
    null
  );
  // hold the draft text of that one “active” reply
  const [previewReplyText, setPreviewReplyText] = useState('');
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);

  //Attachment File Preview
  const [activeAttachment, setActiveAttachment] =
    useState<RunChecklistCommentFile | null>(null);
  const [textPreview, setTextPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!activeAttachment) return;
    const mimeType = activeAttachment.org_file?.file?.mimeType ?? '';

    // only fetch for text/* or CSV
    if (mimeType.startsWith('text/') || mimeType === 'application/json') {
      fetch(activeAttachment.url as string, { mode: 'cors' })
        .then(r => r.text())
        .then(txt => setTextPreview(txt))
        .catch(() => setTextPreview('Could not load preview.'));
    } else {
      setTextPreview(null);
    }
  }, [activeAttachment]);

  const onClickReply = (id: string) => {
    setReplyingCommentId(id);
    setTimeout(() => replyTextareaRef.current?.focus(), 0);
  };
  const onClickCancelReply = () => {
    setReplyingCommentId(null);
    setPreviewReplyText('');
  };

  // Store
  const [isPreview, isPublic, isSingleResponse, runChecklistTree] =
    useRunChecklistStore(
      s => [s.isPreview, s.isPublic, s.isSingleResponse, s.runChecklistTree],
      shallow
    );
  const [selectedOrganization] = useOrganizationStore(
    s => [s.selectedOrganization],
    shallow
  );
  const allTasks = runChecklistTree?.checklist?.checklistsByChecklistId || [];

  // GraphQL
  const { loading, data, error, refetch } = useQuery<RunChecklistComments>(
    GET_RUN_CHECKLIST_COMMENTS_BY_CHECKLIST_ID_QUERY,
    {
      variables: {
        runChecklistId
      },
      fetchPolicy: 'no-cache',
      onCompleted: () => {
        if (data) {
          if (
            data?.run_checklist_comments &&
            data?.run_checklist_comments?.length > 0
          ) {
            const all = data.run_checklist_comments;
            // Parents: those without reply_id
            const parents = all.filter(c => !c.reply_id);
            // Replies: those with reply_id
            const replies = all.filter(c => !!c.reply_id);
            // 3) those replies whose reply_id matches another reply's id
            const replyToReply = replies.filter(r =>
              replies.some(r2 => r2.id === r.reply_id)
            );

            setRunChecklistComments(parents);
            setRunChecklistReplyComments(replies);
            setRunChecklistReplyToReplyComments(replyToReply);
          } else {
            setRunChecklistComments([]);
          }
        }
      }
    }
  );

  const {
    loading: taskLoading,
    data: taskData,
    error: taskError,
    refetch: refetchTaskComments
  } = useQuery<RunChecklistComments>(
    GET_RUN_CHECKLIST_COMMENTS_BY_TASK_ID_QUERY,
    {
      variables: {
        runChecklistId
      },
      fetchPolicy: 'no-cache',
      onCompleted: () => {
        if (taskData) {
          if (
            taskData?.run_checklist_comments &&
            taskData?.run_checklist_comments?.length > 0
          ) {
            const all = taskData.run_checklist_comments;
            // Parents: those without reply_id
            const parents = all.filter(c => !c.reply_id);
            // Replies: those with reply_id
            const replies = all.filter(c => !!c.reply_id);
            // 3) those replies whose reply_id matches another reply's id
            const replyToReply = replies.filter(r =>
              replies.some(r2 => r2.id === r.reply_id)
            );

            setRunTaskComments(parents);
            setRunChecklistTaskReplyComments(replies);
            setRunChecklistTaskReplyToReplyComments(replyToReply);
          } else {
            setRunTaskComments([]);
          }
        }
      }
    }
  );
  const [insertRunChecklistCommentMutation] = useMutation(
    INSERT_RUN_CHECKLIST_COMMENT_MUTATION
  );
  const [deleteRunChecklistCommentMutation] = useMutation(
    DELETE_RUN_CHECKLIST_COMMENT_BY_ID_MUTATION
  );
  const [updateRunChecklistCommentMutation, { loading: updating }] =
    useMutation(UPDATE_RUN_CHECKLIST_COMMENT_MUTATION);

  useEffect(() => {
    () => {
      // Clear the data
      setRunChecklistComments([]);
      setRunChecklistReplyComments([]);
      setRunChecklistReplyToReplyComments([]);
      setRunChecklistTaskReplyToReplyComments([]);
      setRunTaskComments([]);
      setRunChecklistTaskReplyComments([]);
      setSelectedMentionTaskId(null);
      setCommentText('');
      setPreviewText('');
      setUploadedFiles([]);
      setIsCommentSaving(false);
    };
  }, []);

  useEffect(() => {
    if (taskData) {
      if (
        taskData?.run_checklist_comments &&
        taskData?.run_checklist_comments?.length > 0
      ) {
        const all = taskData.run_checklist_comments;
        // Parents: those without reply_id
        const parents = all.filter(c => !c.reply_id);
        // Replies: those with reply_id
        const replies = all.filter(c => !!c.reply_id);
        // 3) those replies whose reply_id matches another reply's id
        const replyToReply = replies.filter(r =>
          replies.some(r2 => r2.id === r.reply_id)
        );

        setRunTaskComments(parents);
        setRunChecklistTaskReplyComments(replies);
        setRunChecklistTaskReplyToReplyComments(replyToReply);
      } else {
        setRunTaskComments([]);
      }
    }
  }, [taskData]);

  useEffect(() => {
    if (!taskLoading && taskError) {
      console.error(taskError);
    }
  }, [taskLoading, taskError]);

  useEffect(() => {
    if (data) {
      if (
        data?.run_checklist_comments &&
        data?.run_checklist_comments?.length > 0
      ) {
        const all = data.run_checklist_comments;
        // Parents: those without reply_id
        const parents = all.filter(c => !c.reply_id);
        // Replies: those with reply_id
        const replies = all.filter(c => !!c.reply_id);
        // 3) those replies whose reply_id matches another reply's id
        const replyToReply = replies.filter(r =>
          replies.some(r2 => r2.id === r.reply_id)
        );

        setRunChecklistComments(parents);
        setRunChecklistReplyToReplyComments(replyToReply);
        setRunChecklistReplyComments(replies);
      } else {
        setRunChecklistComments([]);
      }
    }
  }, [data]);

  function getBgColorForUser(userId: string): string {
    // Simple hash: sum character codes, mod the colors array length
    let sum = 0;
    for (let i = 0; i < userId.length; i++) {
      sum += userId.charCodeAt(i);
    }
    const colors = CommonConstants.DEFAULT_COLORS;
    return colors[sum % colors.length];
  }

  useEffect(() => {
    function attachBgColor<T extends { user?: User }>(arr?: T[]): T[] {
      if (!arr) return [];
      return arr.map(item => {
        if (!item.user?.id) return item;

        const newColor = getBgColorForUser(item.user.id);
        // If it already matches, return original object (so shallow equality holds)
        if (item.user.bgColor === newColor) {
          return item;
        }
        // Otherwise, return a new object with bgColor attached
        return {
          ...item,
          user: {
            ...item.user,
            bgColor: newColor
          }
        };
      });
    }

    // 1) Make new arrays
    const newParents = attachBgColor(runChecklistComments);
    const newReplies = attachBgColor(runChecklistReplyComments);
    const newTaskParents = attachBgColor(runTaskComments);
    const newTaskReplies = attachBgColor(runChecklistTaskReplyComments);
    const newReplyToReply = attachBgColor(runChecklistReplyToReplyComments);
    const newTaskReplyToReply = attachBgColor(
      runChecklistTaskReplyToReplyComments
    );

    let needUpdate = false;
    // 2) Compare old vs. new shallowly. If any index is a different object, we need an update.
    if (
      newParents.length !== (runChecklistComments?.length || 0) ||
      newParents.some((item, i) => item !== runChecklistComments![i])
    ) {
      needUpdate = true;
      setRunChecklistComments(newParents);
    }
    if (
      newReplies.length !== (runChecklistReplyComments?.length || 0) ||
      newReplies.some((item, i) => item !== runChecklistReplyComments![i])
    ) {
      needUpdate = true;
      setRunChecklistReplyComments(newReplies);
    }
    if (
      newReplyToReply.length !==
        (runChecklistReplyToReplyComments?.length || 0) ||
      newReplyToReply.some(
        (item, i) => item !== runChecklistReplyToReplyComments![i]
      )
    ) {
      needUpdate = true;
      setRunChecklistReplyToReplyComments(newReplyToReply);
    }
    if (
      newTaskReplyToReply.length !==
        (runChecklistTaskReplyToReplyComments?.length || 0) ||
      newTaskReplyToReply.some(
        (item, i) => item !== runChecklistTaskReplyToReplyComments![i]
      )
    ) {
      needUpdate = true;
      setRunChecklistTaskReplyToReplyComments(newTaskReplyToReply);
    }
    if (
      newTaskParents.length !== (runTaskComments?.length || 0) ||
      newTaskParents.some((item, i) => item !== runTaskComments![i])
    ) {
      needUpdate = true;
      setRunTaskComments(newTaskParents);
    }
    if (
      newTaskReplies.length !== (runChecklistTaskReplyComments?.length || 0) ||
      newTaskReplies.some(
        (item, i) => item !== runChecklistTaskReplyComments![i]
      )
    ) {
      needUpdate = true;
      setRunChecklistTaskReplyComments(newTaskReplies);
    }
  }, [
    runChecklistComments,
    runTaskComments,
    runChecklistReplyComments,
    runChecklistTaskReplyComments
  ]);

  useEffect(() => {
    if (showCamera && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current
        .play()
        .catch(err => console.warn('Video playback failed:', err));
    }
  }, [showCamera, stream]);

  // Show loading till fetching comments
  if (loading) {
    return (
      <div className="flex gap-2 py-4 animate-pulse">
        {/* Profile image */}
        <div className="w-7 h-7 bg-gray-200 rounded-full"></div>

        {/* Comment block */}
        <div className="flex-1 flex flex-col gap-2">
          {/* Name */}
          <div className="w-2/4 h-4 bg-gray-200 rounded"></div>

          {/* Text */}
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Kick off device camera
  async function openCamera() {
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      setStream(media);
      if (videoRef.current) {
        videoRef.current.srcObject = media;
        await videoRef.current.play();
      }
      setShowCamera(true);
    } catch (err) {
      console.error('Camera permission denied or error:', err);
      alert('Please allow camera access to capture image.');
    }
  }

  function closeCamera() {
    if (stream) {
      stream.getTracks().forEach(t => t.stop());
      setStream(null);
    }
    setShowCamera(false);
  }

  // Snap a photo and feed into upload handler
  function capturePhoto() {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(blob => {
      if (blob) {
        // Create a File so our existing handleFileUpload logic can consume it
        const file = new File([blob], `camera-${Date.now()}.png`, {
          type: 'image/png'
        });
        handleFileUpload({
          target: { files: [file] } as any
        } as React.ChangeEvent<HTMLInputElement>);
      }
      closeCamera();
    }, 'image/png');
  }

  // 1) Recursively find the zero-based index path to a given taskId
  function findPath(
    node: RunChecklistTree,
    target: string,
    path: number[] = []
  ): number[] | null {
    if (node.checklist.run_checklist_task_id === target) return path;
    if (!node.children) return null;
    for (let i = 0; i < node.children.length; i++) {
      const child = node.children[i]!;
      const deeper = findPath(child, target, [...path, i]);
      if (deeper) return deeper;
    }
    return null;
  }

  // 2) Convert that to a “1.2.3.” style prefix
  function getTaskPrefix(taskId: string) {
    if (!runChecklistTree) return '';
    const path = findPath(runChecklistTree, taskId) ?? [];
    if (!path.length) return '';
    return path.map(i => (i + 1).toString()).join('.') + '. ';
  }

  function getMentionIndex(): string {
    if (!mentionPrefix || !selectedMentionTaskId) return '';
    // reuse your findPath logic from CommentRow
    const path = findPath(runChecklistTree!, selectedMentionTaskId) || [];
    if (path.length === 0) return '';
    return path.map(i => i + 1).join('.') + '.';
  }

  // escape helper
  const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // build a regex that matches your prefix literally
  // we need to escape any regex-special chars in prefixNoSpace
  const escapeForRegExp = (s: string) =>
    s.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
  const mentionIndex = getMentionIndex();

  let highlighted = escapeHtml(previewText);

  if (mentionPrefix.trim()) {
    const prefixRe = new RegExp(
      `(${escapeForRegExp(mentionPrefix.trim())})`,
      'g'
    );
    highlighted = highlighted.replace(prefixRe, match => {
      // match === "@1.1. Identification…"
      const full = match.slice(1).trim(); // "1.1. Identification…"
      const index = getMentionIndex(); // "1.1."
      // remove the leading index from `full`
      const rawTitle = full.startsWith(index)
        ? full.slice(index.length).trim()
        : full;
      const escapedTitle = escapeHtml(rawTitle);

      // now you only inject one "@1.1. Title"
      return `<mark class="bg-yellow-100 rounded-md">@${index} ${escapedTitle} </mark>`;
    });
  }

  function CommentRow({
    comment,
    onDelete,
    isSingleResponse,
    runChecklistTree,
    isEditing,
    index,
    isReplying
  }: CommentRowProps) {
    let taskId: string | null = null;
    let isReplyEditing = false;
    let isReplyReplying = false;
    const [localText, setLocalText] = useState(comment.text);
    const [localReplyText, setLocalReplyText] = useState<string>();
    const textareaEditRef = useRef<HTMLTextAreaElement>(null);
    const replyTextareaEditRef = useRef<HTMLTextAreaElement>(null);

    if ((comment.run_checklist as RunChecklist)?.task_id) {
      taskId = (comment.run_checklist as RunChecklist)?.task_id as string;
    } else if (comment?.run_checklist_id) {
      taskId = comment?.run_checklist_id;
    }

    useEffect(() => {
      if (isEditing) {
        setLocalText(comment.text);
        setTimeout(() => textareaEditRef.current?.focus(), 0);
      }
    }, [isEditing, comment.text]);

    useEffect(() => {
      if (isReplying) {
        setTimeout(() => replyTextareaEditRef.current?.focus(), 0);
      }
    }, [isReplying, comment.text]);

    useEffect(() => {
      if (isReplyEditing) {
        setLocalReplyText(localReplyText);
        setTimeout(() => replyTextareaEditRef.current?.focus(), 0);
      }
    }, [isReplyEditing, localReplyText]);

    useEffect(() => {
      if (isReplyReplying) {
        setLocalReplyText(localReplyText);
        setTimeout(() => replyTextareaEditRef.current?.focus(), 0);
      }
    }, [isReplyReplying, localReplyText]);

    // Recursively search the tree for a node whose checklist.task_id === target,
    // returning the path of child‐indices that leads there.
    function findPath(
      node: RunChecklistTree,
      target: string,
      path: number[] = []
    ): number[] | null {
      if (node.checklist.task_id === target) {
        return path; // if root itself matches (unlikely), return []
      }
      if (!node.children) return null;

      for (let i = 0; i < node.children.length; i++) {
        const child = node.children[i]!;
        // if this child matches:
        if (child.checklist.task_id === target) {
          return [...path, i];
        }
        // else, dive deeper:
        const deeper = findPath(child, target, [...path, i]);
        if (deeper) return deeper;
      }
      return null;
    }

    // build our prefix + title
    const idxPath =
      taskId !== null
        ? findPath(runChecklistTree as RunChecklistTree, taskId) ?? []
        : [];
    // convert to 1-based prefix
    const prefix =
      idxPath.length > 0
        ? idxPath.map(i => (i + 1).toString()).join('.') + '.'
        : '';
    // traverse down to leaf to grab its title
    let taskTitle = '…';
    if (idxPath.length > 0) {
      let cursor: RunChecklistTree = runChecklistTree as RunChecklistTree;
      for (const i of idxPath) {
        cursor = cursor.children![i]!;
      }
      taskTitle = cursor.checklist.title as string;
    }

    const onClickTaskEdit = (comment: RunChecklistComment) => {
      setEditingCommentId(comment.id as string);
      setPreviewEditText(comment.text as string);
      // Optionally focus the textarea:
      setTimeout(() => {
        textareaEditRef.current?.focus();
      }, 0);
    };

    async function saveTaskReply(
      parentCommentId: string,
      runChecklistTaskId: string
    ) {
      // Don’t proceed if there’s no text or no runChecklistId

      if (!localReplyText?.trim() || !runChecklistTaskId) {
        return;
      }

      // 1) Generate a new UUID for the reply
      const newReplyId = uuidv4();

      // 2) Build the “run_checklist_comments_insert_input” object for just this one reply
      const newReply: RunChecklistComment = {
        id: newReplyId,
        text: localReplyText?.trim(),
        run_checklist_id: runChecklistTaskId,
        reply_id: parentCommentId
        // created_by, updated_by, etc. will be filled by Hasura/user context
      };

      try {
        // 3) Call the INSERT mutation with empty arrays for orgFiles & commentFiles
        await insertRunChecklistCommentMutation({
          variables: {
            runChecklistComments: [newReply],
            orgFiles: [],
            runChecklistCommentFiles: []
          }
        });

        // 4) Optimistically add the new reply to local state for immediate UI feedback
        // Decide whether that parent comment lives in runChecklistComments or runTaskComments:
        // If parentCommentId exists in runTaskComments, add to runTaskComments; otherwise to runChecklistComments.
        const parentIsTaskComment = runTaskComments?.some(
          c => c.id === parentCommentId
        );

        await refetchTaskComments();
        if (parentIsTaskComment && runTaskComments) {
          // // find the reply’s “UI shape” to insert:
          // const newReplyForUI = {
          //   id: newReplyId,
          //   text: newReply.text,
          //   created_at: new Date().toISOString(),
          //   user: {
          //     id: userData?.id!,
          //     displayName: userData?.displayName!,
          //     email: userData?.email!,
          //     avatarUrl: userData?.avatarUrl!
          //   },
          //   run_checklist_comment_files: [] as any[], // no files on a reply by default
          //   reply_id: parentCommentId
          // };
          // setRunTaskComments([...runTaskComments, newReplyForUI]);
          // refresh that list
        } else if (runChecklistComments) {
          // const newReplyForUI = {
          //   id: newReplyId,
          //   text: newReply.text,
          //   created_at: new Date().toISOString(),
          //   user: {
          //     id: userData?.id!,
          //     displayName: userData?.displayName!,
          //     email: userData?.email!,
          //     avatarUrl: userData?.avatarUrl!
          //   },
          //   run_checklist_comment_files: [] as any[],
          //   reply_id: parentCommentId
          // };
          // setRunChecklistComments([...runChecklistComments, newReplyForUI]);

          // refresh that list
          await refetch();
        }

        // 5) Clear reply state so the textarea disappears
        setPreviewReplyText('');
        setReplyingCommentId(null);
      } catch (error) {
        console.error('Failed to save reply:', error);
      }
    }

    const onClickTaskSave = async (comment: RunChecklistComment) => {
      if (!comment.id) return;
      const isoNow = new Date().toISOString();

      try {
        await updateRunChecklistCommentMutation({
          variables: {
            id: comment.id,
            text: localText?.trim(),
            created_at: isoNow
          }
        });

        // Refetch comments so UI updates
        await refetchTaskComments();
        await refetch();
      } catch (error) {
        console.error('Failed to update comment:', error);
      } finally {
        setEditingCommentId(null);
        setPreviewEditText('');
      }
    };

    const onClickReplyTaskSave = async (comment: RunChecklistComment) => {
      if (!comment.id) return;
      const isoNow = new Date().toISOString();

      try {
        await updateRunChecklistCommentMutation({
          variables: {
            id: comment.id,
            text: localReplyText?.trim(),
            created_at: isoNow
          }
        });

        // Refetch comments so UI updates
        await refetchTaskComments();
        await refetch();
      } catch (error) {
        console.error('Failed to update comment:', error);
      } finally {
        setEditingCommentId(null);
        setPreviewEditText('');
      }
    };

    return (
      <>
        <div className="gap-2 flex group ">
          {/* avatar */}
          <div
            className="w-7 h-7 min-w-[1.75rem] rounded-full text-lg bg-slate-300 font-light text-white flex items-center justify-center uppercase"
            style={{
              backgroundColor: comment.user?.bgColor || 'cl_primary_background'
            }}
          >
            {comment.user?.displayName
              ? comment.user.displayName[0]
              : comment.user?.email![0]}
          </div>

          {/* Comment block */}
          <div className=" flex-1 flex flex-col gap-2 min-w-0">
            {/* Header */}
            <div className=" flex flex-row items-center gap-2 min-h-[22px]">
              {/* header */}
              <div className="flex justify-between items-center w-full">
                {/* Left: Display name */}
                <div className="flex flex-row gap-1">
                  <div className="text-sm font-normal">
                    {comment.user?.displayName}
                  </div>
                  <div className="hidden md:flex items-center pl-2 text-xs text-gray-600 mt-[0.175rem]">
                    {convertISOToLocalDateString(comment.created_at as string)}
                  </div>
                </div>

                {/* Right: Date and Delete */}
                <div className="flex items-center gap-2">
                  {userData?.id === comment?.user?.id && !isEditing && (
                    <>
                      <CLTooltip content="Edit">
                        <CLCustomButton
                          className="hidden group-hover:block p-1 rounded-full hover:bg-slate-100"
                          onClick={() => {
                            onClickTaskEdit(comment);
                          }}
                        >
                          {/* SVG icon */}
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M15.8805 5.10904C16.277 4.71261 16.4998 4.17489 16.4999 3.61418C16.4999 3.05347 16.2773 2.5157 15.8808 2.11917C15.4844 1.72263 14.9467 1.49983 14.386 1.49976C13.8253 1.49969 13.2875 1.72236 12.891 2.11879L2.88145 12.1305C2.70732 12.3042 2.57854 12.5179 2.50645 12.753L1.5157 16.017C1.49632 16.0819 1.49485 16.1508 1.51146 16.2164C1.52807 16.2821 1.56214 16.342 1.61005 16.3898C1.65796 16.4376 1.71792 16.4716 1.78357 16.4881C1.84922 16.5046 1.91812 16.503 1.98295 16.4835L5.2477 15.4935C5.48258 15.4221 5.69633 15.2941 5.8702 15.1208L15.8805 5.10904Z"
                              stroke="#444444"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M11.25 3.75L14.25 6.75"
                              stroke="#444444"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </CLCustomButton>
                      </CLTooltip>
                      <CLTooltip content="Delete">
                        <CLCustomButton
                          className="hidden group-hover:block p-1 rounded-full hover:bg-slate-100"
                          onClick={() => {
                            onDelete(comment);
                          }}
                        >
                          {/* SVG icon */}
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M15.0458 2.72184H10.8458V1.92918C10.8458 1.0921 10.1649 0.411185 9.32785 0.411185L6.67705 0.411133C5.83997 0.411133 5.15918 1.09212 5.15918 1.92913V2.72179L0.958912 2.72184C0.679592 2.72184 0.453125 2.94829 0.453125 3.22762C0.453125 3.50696 0.679578 3.73341 0.958912 3.73341H2.21246V12.2375C2.21246 14.0854 3.71579 15.5886 5.56353 15.5886H10.4411C12.2887 15.5886 13.7922 14.0853 13.7922 12.2375V3.73341H15.0457C15.3251 3.73341 15.5515 3.50696 15.5515 3.22762C15.5517 2.94825 15.3252 2.72184 15.0458 2.72184ZM6.17065 1.92918C6.17065 1.65001 6.39773 1.42288 6.67695 1.42288H9.32789C9.60706 1.42288 9.83419 1.64996 9.83419 1.92918V2.72184H6.17059L6.17065 1.92918ZM12.7808 12.2376C12.7808 13.5275 11.7313 14.5771 10.4413 14.5771H5.56358C4.27363 14.5771 3.22411 13.5276 3.22411 12.2376V3.73345H12.7805L12.7808 12.2376ZM9.22505 11.3746V6.61962C9.22505 6.3403 9.4515 6.11384 9.73083 6.11384C10.0102 6.11384 10.2366 6.34029 10.2366 6.61962V11.3746C10.2366 11.6539 10.0102 11.8803 9.73083 11.8803C9.45146 11.8806 9.22505 11.6539 9.22505 11.3746ZM5.76838 11.3746V6.61962C5.76838 6.3403 5.99483 6.11384 6.27417 6.11384C6.5535 6.11384 6.77995 6.34029 6.77995 6.61962V11.3746C6.77995 11.6539 6.5535 11.8803 6.27417 11.8803C5.99485 11.8806 5.76838 11.6539 5.76838 11.3746Z"
                              fill="#444444"
                            />
                          </svg>
                        </CLCustomButton>
                      </CLTooltip>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="group flex gap-2 p-2 rounded-md pl-3 bg-[#f1f5f9]">
              <div className="flex-1 flex flex-col gap-2 min-w-0">
                <div className="font-normal">
                  {prefix && <span className="mr-1">{prefix}</span>}
                  {taskTitle}
                </div>

                {/* body */}
                {!isEditing ? (
                  <>
                    <div className="flex flex-row">
                      {/* 1) Comment text */}
                      <div className="font-normal w-full">
                        {renderCommentText(comment.text)}
                      </div>

                      {/* 2) Show “Reply” button only if no attachments and not already replying */}
                      {comment?.run_checklist_comment_files?.length === 0 &&
                        !isReplying && (
                          <div className="flex items-center justify-end">
                            <CLCustomButton
                              className="invisible group-hover:visible px-2.5 text-[13px] rounded-full border border-gray-700 hover:bg-slate-100"
                              onClick={() => onClickReply(comment.id!)}
                            >
                              Reply
                            </CLCustomButton>
                          </div>
                        )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex flex-row gap-2 w-full">
                      {/* Text-editing area */}
                      <CLPrimaryCustomTextarea
                        ref={textareaEditRef}
                        style={{
                          backgroundColor: 'white',
                          outline: 'none',
                          boxShadow: 'none',
                          borderRadius: '5px',
                          lineHeight: '1',
                          padding: '0.5rem',
                          fontSize: '14px'
                        }}
                        className="w-full max-h-28 resize-none bg-transparent text-black caret-black"
                        placeholder="Edit comment... (Use @ to mention tasks)"
                        value={localText}
                        rows={1}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                          setLocalText(e.target.value)
                        }
                      />

                      {/* Buttons: cross (cancel) & checkmark (save) */}
                      <div className="flex justify-center items-center gap-2">
                        {/* Cross / Cancel */}
                        <CLCustomButton
                          className="p-1 rounded-full hover:bg-slate-100 bg-white border border-1 border-gray-700"
                          onClick={onClickCancel}
                          disabled={updating}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M18 6L6 18"
                              stroke="#444444"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M6 6L18 18"
                              stroke="#444444"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </CLCustomButton>

                        {/* Checkmark / Save */}
                        <CLCustomButton
                          className="p-1 rounded-full hover:bg-slate-100 bg-white border border-1 border-gray-700"
                          onClick={() => onClickTaskSave(comment)}
                          disabled={updating || localText?.trim().length === 0}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M20 6L9 17L4 12"
                              stroke="#444444"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </CLCustomButton>
                      </div>
                    </div>
                  </>
                )}

                {/* attachments */}
                {(comment?.run_checklist_comment_files ?? []).length > 0 && (
                  <div className="flex flex-col">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 w-full py-2.5 pt-3">
                      {(comment.run_checklist_comment_files ?? []).map(
                        (f, i) => {
                          const mimeType = f.org_file?.file?.mimeType ?? '';
                          const isImage = mimeType.startsWith('image/');
                          return (
                            <div
                              key={i}
                              className="flex gap-2 items-center overflow-hidden w-full bg-white rounded-lg cursor-pointer"
                              onClick={() => setActiveAttachment(f)}
                            >
                              {/* Icon */}
                              <div
                                className={`flex items-center justify-center min-w-[44px] w-24 h-full rounded-l-lg ${
                                  isImage ? 'bg-[#ffb9ff]' : 'bg-[#c8f7c5]'
                                }`}
                              >
                                {isImage ? (
                                  // Image SVG
                                  <img
                                    src={f?.url}
                                    alt={f?.org_file?.file?.name}
                                    className="w-32 h-16 object-cover rounded-l-lg"
                                  />
                                ) : (
                                  // Default File SVG
                                  <svg
                                    width="36"
                                    height="36"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M10.502 1.5V4.5C10.502 4.89782 10.66 5.27936 10.9413 5.56066C11.2226 5.84196 11.6041 6 12.002 6H15.002M7.50195 6.75H6.00195M12.002 9.75H6.00195M12.002 12.75H6.00195M11.252 1.5H4.50195C4.10413 1.5 3.7226 1.65804 3.44129 1.93934C3.15999 2.22064 3.00195 2.60218 3.00195 3V15C3.00195 15.3978 3.15999 15.7794 3.44129 16.0607C3.7226 16.342 4.10413 16.5 4.50195 16.5H13.502C13.8998 16.5 14.2813 16.342 14.5626 16.0607C14.8439 15.7794 15.002 15.3978 15.002 15V5.25L11.252 1.5Z"
                                      stroke="black"
                                      strokeOpacity="0.6"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </div>
                              <div className="flex flex-row justify-between w-full overflow-hidden py-2 gap-2">
                                <div
                                  className="truncate  text-ellipsis"
                                  title={f.org_file?.file?.name}
                                >
                                  {f.org_file?.file?.name}
                                </div>
                                {/* Download Button */}
                                <div className="pr-2.5">
                                  {
                                    <CLTooltip
                                      content="Download"
                                      position="left"
                                    >
                                      <button
                                        className=" bg-[#ffa726] p-1 rounded-full"
                                        onClick={e => {
                                          e.stopPropagation();
                                          downloadImage(
                                            f.url!,
                                            f.org_file?.file?.name
                                          );
                                        }}
                                      >
                                        <svg
                                          width="18"
                                          height="18"
                                          viewBox="0 0 12 12"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M6.00195 8.5V1.5"
                                            stroke="black"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            d="M3.00195 5.5L6.00195 8.5L9.00195 5.5"
                                            stroke="black"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            d="M9.50195 10.5H2.50195"
                                            stroke="black"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </button>
                                    </CLTooltip>
                                  }
                                </div>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                    {/* bottom‐right “Reply” button */}
                    <div className="flex items-end justify-end">
                      <CLCustomButton
                        className="invisible group-hover:visible px-2.5 text-[13px] rounded-full border border-gray-700 hover:bg-slate-100"
                        onClick={() => onClickReply(comment.id!)}
                      >
                        Reply
                      </CLCustomButton>
                    </div>{' '}
                  </div>
                )}
              </div>
            </div>

            {/* 3) When “Reply” is active, render a textarea + cross/tick below */}
            {isReplying && (
              <div className=" ml-6 flex flex-row gap-2 mt-2 w-[97%]">
                {/* Profile image */}
                <div className="flex justify-center items-center">
                  <div
                    className="w-7 h-7 min-w-[1.75rem] rounded-full text-lg text-white flex items-center justify-center uppercase"
                    style={{
                      backgroundColor:
                        runChecklistComments?.find(
                          c => c.user?.id === userData?.id && c.user?.bgColor
                        )?.user?.bgColor ??
                        runTaskComments?.find(
                          c => c.user?.id === userData?.id && c.user?.bgColor
                        )?.user?.bgColor ??
                        runChecklistReplyComments?.find(
                          c => c.user?.id === userData?.id && c.user?.bgColor
                        )?.user?.bgColor ??
                        runChecklistTaskReplyComments?.find(
                          c => c.user?.id === userData?.id && c.user?.bgColor
                        )?.user?.bgColor ??
                        CommonConstants.DEFAULT_COLORS[0]
                    }}
                  >
                    {/* Profile image */}
                    {/* Icon */}
                    {/* {comment?.user?.avatarUrl && (
                      <>
                        <img
                          className="w-7 h-7 rounded-full mx-auto bg-slate-300"
                          src={comment?.user?.avatarUrl}
                          alt=""
                        />
                      </>
                    )} */}

                    {/* Display name */}
                    {userData?.displayName && (
                      <>{userData?.displayName?.charAt(0)}</>
                    )}

                    {/* Email */}
                    {!userData?.displayName && userData?.email && (
                      <>{userData?.email?.charAt(0)}</>
                    )}
                  </div>
                </div>
                <CLPrimaryCustomTextarea
                  ref={replyTextareaEditRef}
                  style={{
                    outline: 'none',
                    boxShadow: 'none',
                    borderRadius: '5px',
                    lineHeight: '1',
                    padding: '0.5rem',
                    fontSize: '14px'
                  }}
                  className="w-full max-h-28 resize-none bg-transparent text-black caret-black"
                  placeholder="Write a reply…"
                  value={localReplyText}
                  rows={1}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setLocalReplyText(e.target.value)
                  }
                />

                {/* Cross (cancel) */}
                <div className="flex justify-center items-center gap-2">
                  <CLCustomButton
                    className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                    onClick={onClickCancelReply}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18"
                        stroke="#444444"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 6L18 18"
                        stroke="#444444"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </CLCustomButton>

                  {/* Checkmark (send/save reply) */}
                  <CLCustomButton
                    className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                    onClick={() => {
                      // call your “save reply” function here, e.g.:

                      saveTaskReply(
                        comment.id as string,
                        comment.run_checklist_id as string
                      );
                      setReplyingCommentId(null);
                      setLocalReplyText('');
                    }}
                    disabled={localReplyText?.trim().length === 0}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20 6L9 17L4 12"
                        stroke="#444444"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </CLCustomButton>
                </div>
              </div>
            )}

            {/* ---- REPLIES: find all replies whose reply_id === comment.id ---- */}
            {runChecklistTaskReplyComments &&
              runChecklistTaskReplyComments?.length > 0 &&
              runChecklistTaskReplyComments
                .filter(r => r.reply_id === comment.id)
                .map(reply => {
                  isReplyEditing = editingCommentId === reply.id;
                  isReplyReplying = replyingCommentId === reply.id;
                  return (
                    <div className="flex flex-col min-w-0" key={reply.id}>
                      <div className="ml-4 mt-2 group/reply flex flex-row gap-2 rounded-md p-2 pr-0">
                        {/* Profile image */}
                        <div
                          className="w-7 h-7 min-w-[1.75rem] cl_primary_background rounded-full text-lg font-light text-white flex items-center justify-center uppercase"
                          style={{
                            backgroundColor:
                              reply.user?.bgColor || 'cl_primary_background'
                          }}
                        >
                          {/* Profile image */}
                          {/* Icon */}
                          {/* {comment?.user?.avatarUrl && (
                                    <>
                                      <img
                                        className="w-7 h-7 rounded-full mx-auto bg-slate-300"
                                        src={comment?.user?.avatarUrl}
                                        alt=""
                                      />
                                    </>
                                  )} */}
                          {/* Display name */}
                          {reply?.user?.displayName && (
                            <>{reply?.user?.displayName?.charAt(0)}</>
                          )}

                          {/* Email */}
                          {!reply?.user?.displayName && reply.user?.email && (
                            <>{reply.user?.email?.charAt(0)}</>
                          )}
                        </div>

                        {/* Reply block */}
                        <div className="flex-1 flex flex-col gap-2">
                          {/* Header */}
                          <div className="flex justify-between min-h-[22px]">
                            {/* User info */}
                            <div className="flex">
                              <div className="font-normal">
                                {reply.user?.displayName}
                              </div>
                              <div className=" items-center pl-2 text-xs text-gray-600 hidden md:flex">
                                {convertISOToLocalDateString(
                                  reply?.created_at || ''
                                )}
                              </div>
                            </div>

                            {/* Delete button */}
                            <div className="flex items-center gap-2">
                              {userData?.id === reply?.user?.id &&
                                !isEditing && (
                                  <>
                                    <CLTooltip content="Edit">
                                      <CLCustomButton
                                        className="hidden group-hover/reply:block p-1 rounded-full hover:bg-slate-100"
                                        onClick={() => {
                                          onClickEdit(reply);
                                        }}
                                      >
                                        {/* SVG icon */}
                                        <svg
                                          width="14"
                                          height="14"
                                          viewBox="0 0 18 18"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M15.8805 5.10904C16.277 4.71261 16.4998 4.17489 16.4999 3.61418C16.4999 3.05347 16.2773 2.5157 15.8808 2.11917C15.4844 1.72263 14.9467 1.49983 14.386 1.49976C13.8253 1.49969 13.2875 1.72236 12.891 2.11879L2.88145 12.1305C2.70732 12.3042 2.57854 12.5179 2.50645 12.753L1.5157 16.017C1.49632 16.0819 1.49485 16.1508 1.51146 16.2164C1.52807 16.2821 1.56214 16.342 1.61005 16.3898C1.65796 16.4376 1.71792 16.4716 1.78357 16.4881C1.84922 16.5046 1.91812 16.503 1.98295 16.4835L5.2477 15.4935C5.48258 15.4221 5.69633 15.2941 5.8702 15.1208L15.8805 5.10904Z"
                                            stroke="#444444"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            d="M11.25 3.75L14.25 6.75"
                                            stroke="#444444"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </CLCustomButton>
                                    </CLTooltip>
                                    <CLTooltip content="Delete">
                                      <CLCustomButton
                                        className="hidden group-hover/reply:block p-1 rounded-full hover:bg-slate-100"
                                        onClick={() => {
                                          deleteComment(reply);
                                        }}
                                      >
                                        {/* SVG icon */}
                                        <svg
                                          width="14"
                                          height="14"
                                          viewBox="0 0 16 16"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M15.0458 2.72184H10.8458V1.92918C10.8458 1.0921 10.1649 0.411185 9.32785 0.411185L6.67705 0.411133C5.83997 0.411133 5.15918 1.09212 5.15918 1.92913V2.72179L0.958912 2.72184C0.679592 2.72184 0.453125 2.94829 0.453125 3.22762C0.453125 3.50696 0.679578 3.73341 0.958912 3.73341H2.21246V12.2375C2.21246 14.0854 3.71579 15.5886 5.56353 15.5886H10.4411C12.2887 15.5886 13.7922 14.0853 13.7922 12.2375V3.73341H15.0457C15.3251 3.73341 15.5515 3.50696 15.5515 3.22762C15.5517 2.94825 15.3252 2.72184 15.0458 2.72184ZM6.17065 1.92918C6.17065 1.65001 6.39773 1.42288 6.67695 1.42288H9.32789C9.60706 1.42288 9.83419 1.64996 9.83419 1.92918V2.72184H6.17059L6.17065 1.92918ZM12.7808 12.2376C12.7808 13.5275 11.7313 14.5771 10.4413 14.5771H5.56358C4.27363 14.5771 3.22411 13.5276 3.22411 12.2376V3.73345H12.7805L12.7808 12.2376ZM9.22505 11.3746V6.61962C9.22505 6.3403 9.4515 6.11384 9.73083 6.11384C10.0102 6.11384 10.2366 6.34029 10.2366 6.61962V11.3746C10.2366 11.6539 10.0102 11.8803 9.73083 11.8803C9.45146 11.8806 9.22505 11.6539 9.22505 11.3746ZM5.76838 11.3746V6.61962C5.76838 6.3403 5.99483 6.11384 6.27417 6.11384C6.5535 6.11384 6.77995 6.34029 6.77995 6.61962V11.3746C6.77995 11.6539 6.5535 11.8803 6.27417 11.8803C5.99485 11.8806 5.76838 11.6539 5.76838 11.3746Z"
                                            fill="#444444"
                                          />
                                        </svg>
                                      </CLCustomButton>
                                    </CLTooltip>
                                  </>
                                )}
                            </div>
                          </div>

                          {/* Text */}
                          {!isReplyEditing ? (
                            <>
                              <div className="flex flex-row mt-2 rounded-md p-2.5 cl_primary_background">
                                {/* 1) Comment text */}
                                <div className="font-normal w-full">
                                  {reply.text}
                                </div>

                                {/* 2) Show “Reply” button only if no attachments and not already replying */}
                                {reply?.run_checklist_comment_files?.length ===
                                  0 &&
                                  !isReplyReplying && (
                                    <div className="hidden group-hover/reply:flex items-center justify-end">
                                      <CLCustomButton
                                        className="px-2.5 text-[13px] rounded-full border border-gray-700 hover:bg-slate-100"
                                        onClick={() => onClickReply(reply.id!)}
                                      >
                                        Reply
                                      </CLCustomButton>
                                    </div>
                                  )}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex flex-row gap-2 w-full">
                                {/* Text-editing area */}
                                <CLPrimaryCustomTextarea
                                  ref={replyTextareaEditRef}
                                  style={{
                                    outline: 'none',
                                    boxShadow: 'none',
                                    borderRadius: '5px',
                                    lineHeight: '1',
                                    padding: '0.5rem',
                                    fontSize: '14px'
                                  }}
                                  className="w-full max-h-28 resize-none bg-transparent text-black caret-black"
                                  placeholder="Edit Reply"
                                  value={localReplyText}
                                  rows={1}
                                  onChange={(
                                    e: React.ChangeEvent<HTMLTextAreaElement>
                                  ) => setLocalReplyText(e.target.value)}
                                />

                                {/* Buttons: cross (cancel) & checkmark (save) */}
                                <div className="flex justify-center items-center gap-2">
                                  {/* Cross / Cancel */}
                                  <CLCustomButton
                                    className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                    onClick={onClickCancel}
                                    disabled={updating}
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M18 6L6 18"
                                        stroke="#444444"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M6 6L18 18"
                                        stroke="#444444"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </CLCustomButton>

                                  {/* Checkmark / Save */}
                                  <CLCustomButton
                                    className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                    onClick={() => onClickReplyTaskSave(reply)}
                                    disabled={
                                      updating ||
                                      localReplyText?.trim().length === 0
                                    }
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M20 6L9 17L4 12"
                                        stroke="#444444"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </CLCustomButton>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* 3) When “Reply” is active, render a textarea + cross/tick below */}
                      {isReplyReplying && (
                        <div className=" ml-[86px] mb-2 flex flex-row gap-2 mt-2 w-[88%]">
                          {/* Profile image */}
                          <div className="flex justify-center items-center">
                            <div
                              className="w-7 h-7 min-w-[1.75rem] rounded-full text-lg text-white flex items-center justify-center uppercase"
                              style={{
                                backgroundColor:
                                  runChecklistComments?.find(
                                    c =>
                                      c.user?.id === userData?.id &&
                                      c.user?.bgColor
                                  )?.user?.bgColor ??
                                  runTaskComments?.find(
                                    c =>
                                      c.user?.id === userData?.id &&
                                      c.user?.bgColor
                                  )?.user?.bgColor ??
                                  runChecklistReplyComments?.find(
                                    c =>
                                      c.user?.id === userData?.id &&
                                      c.user?.bgColor
                                  )?.user?.bgColor ??
                                  runChecklistTaskReplyComments?.find(
                                    c =>
                                      c.user?.id === userData?.id &&
                                      c.user?.bgColor
                                  )?.user?.bgColor ??
                                  CommonConstants.DEFAULT_COLORS[0]
                              }}
                            >
                              {/* Profile image */}
                              {/* Icon */}
                              {/* {comment?.user?.avatarUrl && (
                                <>
                                  <img
                                    className="w-7 h-7 rounded-full mx-auto bg-slate-300"
                                    src={comment?.user?.avatarUrl}
                                    alt=""
                                  />
                                </>
                              )} */}

                              {/* Display name */}
                              {userData?.displayName && (
                                <>{userData?.displayName?.charAt(0)}</>
                              )}

                              {/* Email */}
                              {!userData?.displayName && userData?.email && (
                                <>{userData?.email?.charAt(0)}</>
                              )}
                            </div>
                          </div>
                          <CLPrimaryCustomTextarea
                            ref={replyTextareaEditRef}
                            style={{
                              outline: 'none',
                              boxShadow: 'none',
                              borderRadius: '5px',
                              lineHeight: '1',
                              padding: '0.5rem',
                              fontSize: '14px'
                            }}
                            className="w-full max-h-28 resize-none bg-transparent text-black caret-black"
                            placeholder="Write a reply…"
                            value={localReplyText}
                            rows={1}
                            onChange={(
                              e: React.ChangeEvent<HTMLTextAreaElement>
                            ) => setLocalReplyText(e.target.value)}
                          />

                          {/* Cross (cancel) */}
                          <div className="flex justify-center items-center gap-2">
                            <CLCustomButton
                              className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                              onClick={onClickCancelReply}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M18 6L6 18"
                                  stroke="#444444"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M6 6L18 18"
                                  stroke="#444444"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </CLCustomButton>

                            {/* Checkmark (send/save reply) */}
                            <CLCustomButton
                              className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                              onClick={() => {
                                // call your “save reply” function here, e.g.:

                                saveTaskReply(
                                  reply.id as string,
                                  reply.run_checklist_id as string
                                );
                                setReplyingCommentId(null);
                                setLocalReplyText('');
                              }}
                              disabled={localReplyText?.trim().length === 0}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M20 6L9 17L4 12"
                                  stroke="#444444"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </CLCustomButton>
                          </div>
                        </div>
                      )}

                      {/* ---- REPLY TO REPLIES: find all replies whose reply_id === comment.id ---- */}
                      {runChecklistTaskReplyToReplyComments &&
                        runChecklistTaskReplyToReplyComments?.length > 0 &&
                        runChecklistTaskReplyToReplyComments
                          .filter(r => r.reply_id === reply.id)
                          .map(replyToReply => {
                            const isEditing =
                              editingCommentId === replyToReply.id;
                            return (
                              <div
                                key={replyToReply.id}
                                className="ml-[76px] mt-2 group/reply flex flex-row gap-2 rounded-md p-2 pr-0"
                              >
                                {/* Profile image */}
                                <div
                                  className="w-7 h-7 min-w-[1.75rem] rounded-full text-lg font-light text-white flex items-center justify-center uppercase"
                                  style={{
                                    backgroundColor:
                                      replyToReply.user?.bgColor ||
                                      'cl_primary_background'
                                  }}
                                >
                                  {/* Profile image */}
                                  {/* Icon */}
                                  {/* {comment?.user?.avatarUrl && (
                                    <>
                                      <img
                                        className="w-7 h-7 rounded-full mx-auto bg-slate-300"
                                        src={comment?.user?.avatarUrl}
                                        alt=""
                                      />
                                    </>
                                  )} */}
                                  {/* Display name */}
                                  {replyToReply?.user?.displayName && (
                                    <>
                                      {replyToReply.user?.displayName?.charAt(
                                        0
                                      )}
                                    </>
                                  )}

                                  {/* Email */}
                                  {!replyToReply?.user?.displayName &&
                                    replyToReply.user?.email && (
                                      <>{replyToReply.user?.email?.charAt(0)}</>
                                    )}
                                </div>

                                {/* Reply block */}
                                <div className="flex-1 flex flex-col gap-2">
                                  {/* Header */}
                                  <div className="flex justify-between min-h-[22px]">
                                    {/* User info */}
                                    <div className="flex">
                                      <div className="font-normal">
                                        {replyToReply.user?.displayName}
                                      </div>
                                      <div className="hidden md:flex items-center pl-2 text-xs text-gray-600">
                                        {convertISOToLocalDateString(
                                          replyToReply?.created_at || ''
                                        )}
                                      </div>
                                    </div>

                                    {/* Delete button */}
                                    <div className="flex items-center gap-2">
                                      {userData?.id ===
                                        replyToReply?.user?.id &&
                                        !isEditing && (
                                          <>
                                            <CLTooltip content="Edit">
                                              <CLCustomButton
                                                className="hidden group-hover/reply:block p-1 rounded-full hover:bg-slate-100"
                                                onClick={() => {
                                                  onClickEdit(replyToReply);
                                                }}
                                              >
                                                {/* SVG icon */}
                                                <svg
                                                  width="14"
                                                  height="14"
                                                  viewBox="0 0 18 18"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <path
                                                    d="M15.8805 5.10904C16.277 4.71261 16.4998 4.17489 16.4999 3.61418C16.4999 3.05347 16.2773 2.5157 15.8808 2.11917C15.4844 1.72263 14.9467 1.49983 14.386 1.49976C13.8253 1.49969 13.2875 1.72236 12.891 2.11879L2.88145 12.1305C2.70732 12.3042 2.57854 12.5179 2.50645 12.753L1.5157 16.017C1.49632 16.0819 1.49485 16.1508 1.51146 16.2164C1.52807 16.2821 1.56214 16.342 1.61005 16.3898C1.65796 16.4376 1.71792 16.4716 1.78357 16.4881C1.84922 16.5046 1.91812 16.503 1.98295 16.4835L5.2477 15.4935C5.48258 15.4221 5.69633 15.2941 5.8702 15.1208L15.8805 5.10904Z"
                                                    stroke="#444444"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  />
                                                  <path
                                                    d="M11.25 3.75L14.25 6.75"
                                                    stroke="#444444"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  />
                                                </svg>
                                              </CLCustomButton>
                                            </CLTooltip>
                                            <CLTooltip content="Delete">
                                              <CLCustomButton
                                                className="hidden group-hover/reply:block p-1 rounded-full hover:bg-slate-100"
                                                onClick={() => {
                                                  deleteComment(replyToReply);
                                                }}
                                              >
                                                {/* SVG icon */}
                                                <svg
                                                  width="14"
                                                  height="14"
                                                  viewBox="0 0 16 16"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <path
                                                    d="M15.0458 2.72184H10.8458V1.92918C10.8458 1.0921 10.1649 0.411185 9.32785 0.411185L6.67705 0.411133C5.83997 0.411133 5.15918 1.09212 5.15918 1.92913V2.72179L0.958912 2.72184C0.679592 2.72184 0.453125 2.94829 0.453125 3.22762C0.453125 3.50696 0.679578 3.73341 0.958912 3.73341H2.21246V12.2375C2.21246 14.0854 3.71579 15.5886 5.56353 15.5886H10.4411C12.2887 15.5886 13.7922 14.0853 13.7922 12.2375V3.73341H15.0457C15.3251 3.73341 15.5515 3.50696 15.5515 3.22762C15.5517 2.94825 15.3252 2.72184 15.0458 2.72184ZM6.17065 1.92918C6.17065 1.65001 6.39773 1.42288 6.67695 1.42288H9.32789C9.60706 1.42288 9.83419 1.64996 9.83419 1.92918V2.72184H6.17059L6.17065 1.92918ZM12.7808 12.2376C12.7808 13.5275 11.7313 14.5771 10.4413 14.5771H5.56358C4.27363 14.5771 3.22411 13.5276 3.22411 12.2376V3.73345H12.7805L12.7808 12.2376ZM9.22505 11.3746V6.61962C9.22505 6.3403 9.4515 6.11384 9.73083 6.11384C10.0102 6.11384 10.2366 6.34029 10.2366 6.61962V11.3746C10.2366 11.6539 10.0102 11.8803 9.73083 11.8803C9.45146 11.8806 9.22505 11.6539 9.22505 11.3746ZM5.76838 11.3746V6.61962C5.76838 6.3403 5.99483 6.11384 6.27417 6.11384C6.5535 6.11384 6.77995 6.34029 6.77995 6.61962V11.3746C6.77995 11.6539 6.5535 11.8803 6.27417 11.8803C5.99485 11.8806 5.76838 11.6539 5.76838 11.3746Z"
                                                    fill="#444444"
                                                  />
                                                </svg>
                                              </CLCustomButton>
                                            </CLTooltip>
                                          </>
                                        )}
                                    </div>
                                  </div>

                                  {/* Text */}
                                  {!isEditing ? (
                                    <>
                                      <div className="flex flex-row mt-2">
                                        {/* 1) Comment text */}
                                        <div className="font-normal w-full rounded-md p-2.5 cl_primary_background">
                                          {replyToReply.text}
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="flex flex-row gap-2 w-full">
                                        {/* Text-editing area */}
                                        <CLPrimaryCustomTextarea
                                          ref={textareaRef}
                                          style={{
                                            outline: 'none',
                                            boxShadow: 'none',
                                            borderRadius: '5px',
                                            lineHeight: '1',
                                            padding: '0.5rem',
                                            fontSize: '14px'
                                          }}
                                          className="w-full max-h-28 resize-none bg-transparent text-black caret-black"
                                          placeholder="Edit comment... (Use @ to mention tasks)"
                                          value={previewEditText}
                                          rows={1}
                                          onChange={(
                                            e: React.ChangeEvent<HTMLTextAreaElement>
                                          ) =>
                                            setPreviewEditText(e.target.value)
                                          }
                                        />

                                        {/* Buttons: cross (cancel) & checkmark (save) */}
                                        <div className="flex justify-center items-center gap-2">
                                          {/* Cross / Cancel */}
                                          <CLCustomButton
                                            className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                            onClick={onClickCancel}
                                            disabled={updating}
                                          >
                                            <svg
                                              width="16"
                                              height="16"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <path
                                                d="M18 6L6 18"
                                                stroke="#444444"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                              <path
                                                d="M6 6L18 18"
                                                stroke="#444444"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </CLCustomButton>

                                          {/* Checkmark / Save */}
                                          <CLCustomButton
                                            className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                            onClick={() =>
                                              onClickSave(replyToReply)
                                            }
                                            disabled={
                                              updating ||
                                              previewEditText.trim().length ===
                                                0
                                            }
                                          >
                                            <svg
                                              width="16"
                                              height="16"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <path
                                                d="M20 6L9 17L4 12"
                                                stroke="#444444"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </CLCustomButton>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                    </div>
                  );
                })}
          </div>
        </div>
      </>
    );
  }

  async function addComment(targetChecklistId?: string | null) {
    // Do a validation
    if (commentText || uploadedFiles.length > 0) {
      // Set the saving flag
      setIsCommentSaving(true);

      const newCommentId = uuidv4();
      const newRunChecklistComment: RunChecklistComment = {
        id: newCommentId,
        text: commentText || '',
        run_checklist_id: targetChecklistId || runChecklistId
      };

      // Collect uploaded files data
      const {
        orgFiles,
        runChecklistCommentFiles,
        runChecklistCommentFilesForUI
      } = await uploadFilesToFileStorage(newCommentId);

      // Insert the comment to database
      await insertRunChecklistCommentMutation({
        variables: {
          runChecklistComments: [newRunChecklistComment],
          orgFiles: orgFiles,
          runChecklistCommentFiles: runChecklistCommentFiles
        }
      });

      // Add the comment to the list, it will refresh the UI
      if (runChecklistComments && runTaskComments) {
        if (!targetChecklistId) {
          setRunChecklistComments([
            ...runChecklistComments,
            {
              ...newRunChecklistComment,
              ...{
                run_checklist_comment_files: runChecklistCommentFilesForUI
              },
              ...{
                created_at: new Date().toISOString(),
                user: {
                  id: userData?.id,
                  displayName: userData?.displayName,
                  email: userData?.email,
                  avatarUrl: userData?.avatarUrl
                }
              }
            }
          ]);
          await refetchTaskComments();
          await refetch();
        } else if (targetChecklistId) {
          // setRunTaskComments([
          //   ...runTaskComments,
          //   {
          //     ...newRunChecklistComment,
          //     ...{
          //       run_checklist_comment_files: runChecklistCommentFilesForUI
          //     },
          //     ...{
          //       created_at: new Date().toISOString(),
          //       user: {
          //         id: userData?.id,
          //         displayName: userData?.displayName,
          //         email: userData?.email,
          //         avatarUrl: userData?.avatarUrl
          //       }
          //     }
          //   }
          // ]);

          await refetchTaskComments();
        }
      }

      // Clear the comment text
      setCommentText('');
      setPreviewText('');
      setUploadedFiles([]);
      setIsCommentSaving(false);
      setSelectedMentionTaskId(null);
    }
  }

  async function saveReply(parentCommentId: string) {
    // Don’t proceed if there’s no text or no runChecklistId
    if (!previewReplyText.trim() || !runChecklistId) {
      return;
    }

    // 1) Generate a new UUID for the reply
    const newReplyId = uuidv4();

    // 2) Build the “run_checklist_comments_insert_input” object for just this one reply
    const newReply: RunChecklistComment = {
      id: newReplyId,
      text: previewReplyText.trim(),
      run_checklist_id: runChecklistId,
      reply_id: parentCommentId
      // created_by, updated_by, etc. will be filled by Hasura/user context
    };

    try {
      // 3) Call the INSERT mutation with empty arrays for orgFiles & commentFiles
      await insertRunChecklistCommentMutation({
        variables: {
          runChecklistComments: [newReply],
          orgFiles: [],
          runChecklistCommentFiles: []
        }
      });

      // 4) Optimistically add the new reply to local state for immediate UI feedback
      // Decide whether that parent comment lives in runChecklistComments or runTaskComments:
      // If parentCommentId exists in runTaskComments, add to runTaskComments; otherwise to runChecklistComments.
      const parentIsTaskComment = runTaskComments?.some(
        c => c.id === parentCommentId
      );

      if (parentIsTaskComment && runTaskComments) {
        // find the reply’s “UI shape” to insert:
        // const newReplyForUI = {
        //   id: newReplyId,
        //   text: newReply.text,
        //   created_at: new Date().toISOString(),
        //   user: {
        //     id: userData?.id!,
        //     displayName: userData?.displayName!,
        //     email: userData?.email!,
        //     avatarUrl: userData?.avatarUrl!
        //   },
        //   run_checklist_comment_files: [] as any[], // no files on a reply by default
        //   reply_id: parentCommentId
        // };
        // setRunTaskComments([...runTaskComments, newReplyForUI]);

        // refresh that list
        await refetchTaskComments();
      } else if (runChecklistComments) {
        // const newReplyForUI = {
        //   id: newReplyId,
        //   text: newReply.text,
        //   created_at: new Date().toISOString(),
        //   user: {
        //     id: userData?.id!,
        //     displayName: userData?.displayName!,
        //     email: userData?.email!,
        //     avatarUrl: userData?.avatarUrl!
        //   },
        //   run_checklist_comment_files: [] as any[],
        //   reply_id: parentCommentId
        // };
        // setRunChecklistComments([...runChecklistComments, newReplyForUI]);

        // refresh that list
        await refetch();
      }

      // 5) Clear reply state so the textarea disappears
      setPreviewReplyText('');
      setReplyingCommentId(null);
    } catch (error) {
      console.error('Failed to save reply:', error);
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // if (e.key === 'Enter') {
    //   e.preventDefault();
    //   e.stopPropagation();
    //   addComment();
    // }
  };

  async function deleteComment(deleteComment: RunChecklistComment) {
    if (deleteComment?.id) {
      // Delete the comment from database
      await deleteRunChecklistCommentMutation({
        variables: {
          runChecklistCommentId: deleteComment?.id
        }
      });

      // Remove the comment from the list, it will refresh the UI
      if (runChecklistComments) {
        // setRunChecklistComments([
        //   ...runChecklistComments.slice(0, commentIndex),
        //   ...runChecklistComments.slice(commentIndex + 1)
        // ]);

        await refetchTaskComments();
        await refetch();
      }
    }
  }

  const onClickEdit = (comment: RunChecklistComment) => {
    setEditingCommentId(comment.id as string);
    setPreviewEditText(comment.text as string);
    // Optionally focus the textarea:
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const onClickSave = async (comment: RunChecklistComment) => {
    if (!comment.id) return;
    const isoNow = new Date().toISOString();

    try {
      await updateRunChecklistCommentMutation({
        variables: {
          id: comment.id,
          text: previewEditText.trim(),
          created_at: isoNow
        }
      });

      // Refetch comments so UI updates
      await refetchTaskComments();
      await refetch();
    } catch (error) {
      console.error('Failed to update comment:', error);
    } finally {
      setEditingCommentId(null);
      setPreviewEditText('');
    }
  };

  const onClickCancel = () => {
    setEditingCommentId(null);
    setPreviewEditText('');
  };

  // Upload File
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);

      // Iterate through each file and refresh the UI
      for (const file of files) {
        // Only for image add preview url
        uploadedFiles.push({
          file,
          previewUrl: file.type.includes('image')
            ? URL.createObjectURL(file)
            : ''
        });
      }
      setUploadedFiles([...uploadedFiles]);
    }
  };

  // Delete File
  const deleteUploadedFile = (index: number) => {
    uploadedFiles.splice(index, 1);
    setUploadedFiles([...uploadedFiles]);
  };

  // Upload the files to storage
  const uploadFilesToFileStorage = async (newCommentId: string) => {
    const orgFiles: OrgFile[] = [];
    const runChecklistCommentFiles: RunChecklistCommentFile[] = [];
    const runChecklistCommentFilesForUI: RunChecklistCommentFile[] = [];

    // Iterate through each file and upload
    if (uploadedFiles && uploadedFiles.length > 0) {
      for (const uploadedFile of uploadedFiles) {
        if (uploadedFile.file) {
          const uploadedFileInfo = await upload({ file: uploadedFile.file });

          if (uploadedFileInfo.id) {
            const publicUrl = await nhost.storage.getPublicUrl({
              fileId: uploadedFileInfo.id
            });

            // Insert a file component
            const orgFileId = uuidv4();
            const runChecklistCommentFileId = uuidv4();
            const orgFile = {
              id: orgFileId,
              org_id: selectedOrganization?.id,
              file_id: uploadedFileInfo.id
            };
            orgFiles.push(orgFile);
            const runChecklistCommentFile = {
              id: runChecklistCommentFileId,
              url: publicUrl,
              run_checklist_comment_id: newCommentId,
              org_file_id: orgFileId
            };
            runChecklistCommentFiles.push(runChecklistCommentFile);

            // This is just to show the file in UI
            runChecklistCommentFilesForUI.push({
              ...runChecklistCommentFile,
              org_file: {
                ...orgFile,
                ...{
                  file: {
                    id: uploadedFileInfo.id,
                    name: uploadedFile.file?.name,
                    mimeType: uploadedFile.file?.type
                  }
                }
              }
            });
          }
        }
      }
    }

    return {
      orgFiles,
      runChecklistCommentFiles,
      runChecklistCommentFilesForUI
    };
  };

  /**
   * Render comment text that may include literal "\n" escapes and escaped quotes \".
   * Normalizes common escape sequences, converts **bold** -> <strong>, auto-links URLs.
   */
  function renderCommentText(text?: string): React.ReactNode {
    if (!text) return null;

    // ---- 1) Normalize escaped sequences ----
    // Order matters: handle \\n before \\" so sequences are converted correctly.
    const normalized = String(text)
      // Convert escaped newlines (two characters \ and n) into actual newline
      .replace(/\\n/g, '\n')
      // Remove escaped carriage returns
      .replace(/\\r/g, '')
      // Convert escaped tabs if present
      .replace(/\\t/g, '\t')
      // Convert escaped double-quotes \" => "
      .replace(/\\"/g, '"')
      // Optional: collapse double-escaped backslashes \\ -> \ (if you see \\ in text)
      .replace(/\\\\/g, '\\');

    // ---- 2) Regexes for inline formatting ----
    const boldRe = /\*\*(.+?)\*\*/g; // **bold**
    const urlRe = /https?:\/\/[^\s)]+/g; // crude URL matcher

    // ---- 3) Split into lines and render ----
    const lines = normalized.split('\n');

    return (
      <>
        {lines.map((line, lineIdx) => {
          if (line.trim() === '') {
            // preserve paragraph spacing
            return <br key={`empty-${lineIdx}`} />;
          }

          // find bold spans
          const fragments: React.ReactNode[] = [];
          let lastIndex = 0;
          let m: RegExpExecArray | null;
          boldRe.lastIndex = 0;
          while ((m = boldRe.exec(line)) !== null) {
            if (m.index > lastIndex) {
              fragments.push(line.slice(lastIndex, m.index));
            }
            fragments.push(
              <strong key={`b-${lineIdx}-${m.index}`} className="font-semibold">
                {m[1]}
              </strong>
            );
            lastIndex = m.index + m[0].length;
          }
          if (lastIndex < line.length) fragments.push(line.slice(lastIndex));

          // for fragments that are plain strings, split out URLs
          const inlineNodes: React.ReactNode[] = [];
          fragments.forEach((frag, fragIdx) => {
            if (typeof frag !== 'string') {
              inlineNodes.push(
                <React.Fragment key={`node-${lineIdx}-${fragIdx}`}>
                  {frag}
                </React.Fragment>
              );
              return;
            }

            let last = 0;
            let um: RegExpExecArray | null;
            urlRe.lastIndex = 0;
            while ((um = urlRe.exec(frag)) !== null) {
              if (um.index > last) {
                inlineNodes.push(
                  <React.Fragment key={`txt-${lineIdx}-${fragIdx}-${last}`}>
                    {frag.slice(last, um.index)}
                  </React.Fragment>
                );
              }
              const url = um[0];
              inlineNodes.push(
                <a
                  key={`url-${lineIdx}-${fragIdx}-${um.index}`}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline break-all"
                >
                  {url}
                </a>
              );
              last = um.index + url.length;
            }
            if (last < frag.length) {
              inlineNodes.push(
                <React.Fragment key={`txtend-${lineIdx}-${fragIdx}`}>
                  {frag.slice(last)}
                </React.Fragment>
              );
            }
          });

          return (
            <div key={`line-${lineIdx}`} className="whitespace-pre-wrap">
              {inlineNodes}
            </div>
          );
        })}
      </>
    );
  }

  return (
    <>
      {
        <>
          <hr
            className={
              `mb-4 border-t border-gray-300 flex-grow ` +
              (!showCommentText ? ' !mb-2' : '')
            }
          />
        </>
      }

      {/* Add Comment Button */}
      {!(runChecklistComments?.length || runTaskComments?.length) &&
        !isSingleResponse &&
        !showCommentText && (
          <div className="flex justify-start px-6 pt-2 pb-2">
            <CLPrimaryCustomButton
              className="!bg-white hover:!bg-gray-300 text-xs gap-2 !h-[38px] px-3 py-2 !text-[14px]"
              onClick={() => setShowCommentText(true)} // Trigger the comment box to appear
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.002 10C14.002 10.3536 13.8615 10.6928 13.6114 10.9428C13.3614 11.1929 13.0222 11.3333 12.6686 11.3333H4.66862L2.00195 14V3.33333C2.00195 2.97971 2.14243 2.64057 2.39248 2.39052C2.64253 2.14048 2.98166 2 3.33529 2H12.6686C13.0222 2 13.3614 2.14048 13.6114 2.39052C13.8615 2.64057 14.002 2.97971 14.002 3.33333V10Z"
                  stroke="black"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Add a Comment
            </CLPrimaryCustomButton>
          </div>
        )}

      {/* Add new comment */}
      {!isSingleResponse && showCommentText && (
        <>
          <div className="flex gap-2 px-6 pb-4">
            {/* Profile image */}
            <div
              className="mt-0 w-7 h-7 min-w-[1.75rem] cl_primary_background rounded-full text-lg text-white flex items-center justify-center uppercase"
              style={{
                backgroundColor:
                  runChecklistComments?.find(
                    c => c.user?.id === userData?.id && c.user?.bgColor
                  )?.user?.bgColor ??
                  runTaskComments?.find(
                    c => c.user?.id === userData?.id && c.user?.bgColor
                  )?.user?.bgColor ??
                  runChecklistReplyComments?.find(
                    c => c.user?.id === userData?.id && c.user?.bgColor
                  )?.user?.bgColor ??
                  runChecklistTaskReplyComments?.find(
                    c => c.user?.id === userData?.id && c.user?.bgColor
                  )?.user?.bgColor ??
                  CommonConstants.DEFAULT_COLORS[0]
              }}
            >
              {/* Profile image */}
              {/* Icon */}

              {/* Display name */}
              {userData?.displayName && <>{userData?.displayName?.charAt(0)}</>}

              {/* Email */}
              {!userData?.displayName && userData?.email && (
                <>{userData?.email?.charAt(0)}</>
              )}
            </div>

            <div className="flex-1 flex flex-col gap-2">
              <div className="relative w-full border rounded-lg focus-within:border-yellow-400">
                <div
                  aria-hidden="true"
                  className="
                    absolute inset-0
                    text-base
                    m-4
                    mb-[7rem]
                    ml-[0.7rem]
                    mt-[0.7rem]
                    whitespace-pre-wrap
                    break-words
                    text-gray-900
                    pointer-events-none
                    rounded-md
                  "
                  // dangerously set HTML to get the <mark> tags rendered
                  dangerouslySetInnerHTML={{ __html: highlighted + '&nbsp;' }}
                />
                {/* Comment text area */}
                <CLPrimaryCustomTextarea
                  ref={textareaRef}
                  style={{
                    border: 'none',
                    outline: 'none',
                    boxShadow: 'none',
                    borderRadius: '0px',
                    lineHeight: '1.4',
                    padding: '0.75rem 1rem 0rem 1rem'
                  }}
                  className="w-full max-h-28 resize-none bg-transparent text-transparent caret-black"
                  placeholder={'Add a comment...(Use @ to mention tasks)'}
                  value={previewText}
                  rows={3}
                  onKeyDown={handleKeyDown}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const val = event.target.value;
                    setPreviewText(val);
                    if (mentionPrefix) {
                      // strip out the stored mentionPrefix only once (from the *start* of val)
                      const stripped = val.startsWith(mentionPrefix)
                        ? val.slice(mentionPrefix.length)
                        : val;
                      setCommentText(stripped);
                    } else {
                      setCommentText(val);
                    }

                    // detect a trailing “@…”
                    const m = val.match(/(?:^|\s)@([\w]*)$/);
                    if (m) {
                      const q = m[1].toLowerCase();
                      setMentionQuery(q);
                      // filter your tasks:

                      setMentionResults(
                        allTasks.filter(t => t.title?.toLowerCase().includes(q))
                      );
                    } else {
                      setMentionQuery(null);
                    }
                  }}
                ></CLPrimaryCustomTextarea>

                {mentionQuery !== null && mentionResults.length > 0 && (
                  <ul className="absolute bg-white left-0 bottom-32 mb-2 border rounded shadow-md max-h-28 overflow-y-auto z-20 w-[60%]">
                    {mentionResults.map((task, index) => {
                      const prefix = getTaskPrefix(
                        task.run_checklist_task_id as string
                      );

                      return (
                        <li
                          key={task.task_id}
                          className={`px-2 py-1 hover:bg-gray-100 cursor-pointer ${
                            index === 0 ? 'bg-gray-100' : ''
                          }`}
                          onMouseDown={e => {
                            e.preventDefault(); // keep focus in textarea

                            // 1) compute the numeric prefix + title
                            const idx = getTaskPrefix(
                              task.run_checklist_task_id as string
                            ); // e.g. "2.3. "
                            const fullMentionPrefix = `@${idx}${task.title} `; // "@2.3. My Task Title "

                            // 2) replace the trailing "@…" in previewText with your full mention
                            const fullMention = previewText.replace(
                              /@\S*$/,
                              fullMentionPrefix
                            );

                            // 3) strip out exactly that fullMentionPrefix from the start to get your comment body
                            const stripped = fullMention.startsWith(
                              fullMentionPrefix
                            )
                              ? fullMention.slice(fullMentionPrefix.length)
                              : fullMention;

                            // 4) update all the React state
                            setPreviewText(fullMention);
                            setCommentText(stripped);
                            setMentionPrefix(fullMentionPrefix);
                            setSelectedMentionTaskId(
                              task.run_checklist_task_id as string
                            );
                            setMentionQuery(null);
                          }}
                        >
                          {/* Show prefix (e.g. “2.3. ”) before the title */}
                          {prefix && (
                            <span className="font-medium">{prefix}</span>
                          )}
                          {task.title}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {/* Attachment preview */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-2 gap-y-4 p-3 pb-0">
                  {uploadedFiles.map((uploadedFile, index) => (
                    <div key={index} className="flex items-start w-full">
                      <div className="group flex justify-between items-center gap-2 border border-black/10 rounded-lg pr-2 w-full">
                        {/* File data */}
                        <div className="flex gap-2 items-center overflow-hidden w-full">
                          {/* Icon */}
                          {uploadedFile?.previewUrl ? (
                            <img
                              src={uploadedFile.previewUrl}
                              className="w-32 h-16 rounded-l-lg object-cover"
                            />
                          ) : (
                            <div className="flex items-center justify-center w-24 h-16 rounded-l-lg bg-[#c8f7c5]">
                              <svg
                                height="50%"
                                width="50%"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10.502 1.5V4.5C10.502 4.89782 10.66 5.27936 10.9413 5.56066C11.2226 5.84196 11.6041 6 12.002 6H15.002M7.50195 6.75H6.00195M12.002 9.75H6.00195M12.002 12.75H6.00195M11.252 1.5H4.50195C4.10413 1.5 3.7226 1.65804 3.44129 1.93934C3.15999 2.22064 3.00195 2.60218 3.00195 3V15C3.00195 15.3978 3.15999 15.7794 3.44129 16.0607C3.7226 16.342 4.10413 16.5 4.50195 16.5H13.502C13.8998 16.5 14.2813 16.342 14.5626 16.0607C14.8439 15.7794 15.002 15.3978 15.002 15V5.25L11.252 1.5Z"
                                  stroke="black"
                                  strokeOpacity="0.6"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>{' '}
                            </div>
                          )}

                          {/* Details */}
                          <div className="overflow-hidden flex flex-row items-center justify-between w-full py-2">
                            <div className="line-clamp-1">
                              {uploadedFile.file.name}
                            </div>
                            <div className="uppercase text-xs font-medium text-slate-600">
                              {(() => {
                                const parts = uploadedFile.file.name.split('.');
                                return parts.length > 1
                                  ? parts[parts.length - 1]
                                  : '';
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Delete icon */}
                      <div className="relative right-3 bottom-3">
                        <div
                          className="w-6 h-6 rounded-full bg-gray-600 cursor-pointer flex items-center justify-center"
                          onClick={() => {
                            deleteUploadedFile(index);
                          }}
                        >
                          <svg
                            width="8"
                            height="8"
                            viewBox="0 0 8 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M0.574173 0.575638C0.808488 0.341324 1.18839 0.341324 1.4227 0.575638L3.99844 3.15137L6.57417 0.575638C6.80849 0.341324 7.18839 0.341324 7.4227 0.575638C7.65702 0.809953 7.65702 1.18985 7.4227 1.42417L4.84697 3.9999L7.4227 6.57564C7.65702 6.80995 7.65702 7.18985 7.4227 7.42417C7.18839 7.65848 6.80849 7.65848 6.57417 7.42417L3.99844 4.84843L1.4227 7.42417C1.18839 7.65848 0.808488 7.65848 0.574173 7.42417C0.339859 7.18985 0.339859 6.80995 0.574173 6.57564L3.14991 3.9999L0.574173 1.42417C0.339859 1.18985 0.339859 0.809953 0.574173 0.575638Z"
                              fill="white"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <hr className="my-4 mb-0 border-t border-gray-300 w-full flex-grow" />
                <div className="flex justify-between px-4 py-3 pb-1">
                  {/* Left side */}
                  <div className="flex gap-2">
                    {/* Attachment icon */}
                    <CLTooltip content="Attachments">
                      <CLCustomButton>
                        <label htmlFor="comment-attachment-upload">
                          <svg
                            className="cursor-pointer rounded-full border border-gray-300 hover:bg-slate-100 p-1"
                            xmlns="http://www.w3.org/2000/svg"
                            height="28"
                            viewBox="0 -960 960 960"
                            width="28"
                          >
                            <path
                              d="M460-80q-92 0-156-64t-64-156v-420q0-66 47-113t113-47q66 0 113 47t47 113v380q0 42-29 71t-71 29q-42 0-71-29t-29-71v-350q0-13 8.5-21.5T390-720q13 0 21.5 8.5T420-690v350q0 17 11.5 28.5T460-300q17 0 28.5-11.5T500-340v-380q0-42-29-71t-71-29q-42 0-71 29t-29 71v420q0 66 47 113t113 47q66 0 113-47t47-113v-390q0-13 8.5-21.5T650-720q13 0 21.5 8.5T680-690v390q0 92-64 156T460-80Z"
                              fill="black"
                              fillOpacity="0.6"
                            />
                          </svg>
                        </label>
                        <input
                          className="hidden"
                          id="comment-attachment-upload"
                          type="file"
                          multiple
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            handleFileUpload(event);
                          }}
                        />
                      </CLCustomButton>
                    </CLTooltip>

                    {/* Capture Image icon */}
                    <CLTooltip content="Capture Image">
                      <CLCustomButton onClick={openCamera}>
                        {/* camera SVG */}
                        <svg
                          className="cursor-pointer rounded-full border border-gray-300 hover:bg-slate-100 p-1"
                          width="28"
                          height="28"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M11.75 4H8.25L6.5 6.0625H4.4C4.0287 6.0625 3.6726 6.20737 3.41005 6.46523C3.1475 6.72309 3 7.07283 3 7.4375V13.625C3 13.9897 3.1475 14.3394 3.41005 14.5973C3.6726 14.8551 4.0287 15 4.4 15H15.6C15.9713 15 16.3274 14.8551 16.5899 14.5973C16.8525 14.3394 17 13.9897 17 13.625V7.4375C17 7.07283 16.8525 6.72309 16.5899 6.46523C16.3274 6.20737 15.9713 6.0625 15.6 6.0625H13.5L11.75 4Z"
                            stroke="black"
                            strokeOpacity="0.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10 12.25C11.1598 12.25 12.1 11.3266 12.1 10.1875C12.1 9.04841 11.1598 8.125 10 8.125C8.8402 8.125 7.9 9.04841 7.9 10.1875C7.9 11.3266 8.8402 12.25 10 12.25Z"
                            stroke="black"
                            strokeOpacity="0.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </CLCustomButton>
                    </CLTooltip>
                  </div>

                  {/* Right side */}
                  <div>
                    {/* Comment button */}
                    <CLPrimaryCustomButton
                      className="text-xs px-2 py-1 font-medium"
                      onClick={() => {
                        addComment(selectedMentionTaskId);
                      }}
                      disabled={
                        (!commentText && uploadedFiles?.length == 0) ||
                        isCommentSaving
                      }
                    >
                      Comment
                    </CLPrimaryCustomButton>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showCamera && (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
              onClick={closeCamera}
            >
              <div
                className="bg-white rounded-lg p-4 relative"
                onClick={e => e.stopPropagation()}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-auto h-auto bg-black rounded-md"
                />
                <div className="flex justify-end gap-2 mt-2">
                  <CLButton
                    className="px-4 py-2 bg-gray-200 rounded-full"
                    onClick={closeCamera}
                  >
                    Cancel
                  </CLButton>
                  <CLPrimaryCustomButton
                    className="px-2 py-1 font-medium"
                    onClick={capturePhoto}
                  >
                    Capture
                  </CLPrimaryCustomButton>
                </div>
                <canvas ref={canvasRef} className="hidden" />
              </div>
            </div>
          )}
        </>
      )}

      {runChecklistComments?.length || runTaskComments?.length ? (
        <>
          <div
            className={
              `flex items-start gap-4 px-5 ` +
              (showCommentText ? 'flex-col' : 'flex-row !items-center')
            }
          >
            {showCommentText && (
              <hr className="mr-4 w-full my-4 mb-0 border-t border-gray-300 flex-grow" />
            )}
            <h3 className={`text-gray-600 whitespace-nowrap ml-0 mb-3`}>
              Comments (
              {(runChecklistComments?.length || 0) +
                (runTaskComments?.length || 0)}
              )
            </h3>

            {/* Add Comment Button */}
            {!isSingleResponse && !showCommentText && (
              <div className="flex justify-start px-0 pb-3">
                <CLPrimaryCustomButton
                  className="!bg-white hover:!bg-gray-300 text-xs gap-2 !h-[32px] px-3 py-1 text-[14px]"
                  onClick={() => setShowCommentText(true)} // Trigger the comment box to appear
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.002 10C14.002 10.3536 13.8615 10.6928 13.6114 10.9428C13.3614 11.1929 13.0222 11.3333 12.6686 11.3333H4.66862L2.00195 14V3.33333C2.00195 2.97971 2.14243 2.64057 2.39248 2.39052C2.64253 2.14048 2.98166 2 3.33529 2H12.6686C13.0222 2 13.3614 2.14048 13.6114 2.39052C13.8615 2.64057 14.002 2.97971 14.002 3.33333V10Z"
                      stroke="black"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Add a Comment
                </CLPrimaryCustomButton>
              </div>
            )}
          </div>

          <div
            className="gap-2 mx-0 md:mx-6 flex flex-col p-4 mb-4 rounded-md"
            style={{ boxShadow: '0 0 8px rgba(0,0,0,0.1)' }}
          >
            {/*List of Task Comments */}
            {runTaskComments?.length ? (
              <div className="flex flex-col gap-4 mb-4">
                {runTaskComments.map((comment, index) => {
                  const isEditing = editingCommentId === comment.id;
                  const isReplying = replyingCommentId === comment.id;
                  return (
                    <CommentRow
                      key={comment.id}
                      comment={comment}
                      runChecklistTree={runChecklistTree as RunChecklistTree}
                      onDelete={c => deleteComment(c)}
                      isSingleResponse={isSingleResponse}
                      isEditing={isEditing}
                      isReplying={isReplying}
                      index={index}
                    />
                  );
                })}
              </div>
            ) : null}

            {/* List of Checklist comments */}
            {runChecklistComments?.length ? (
              <div className="flex flex-col gap-4 mb-4">
                {runChecklistComments?.map((comment, commentIndex) => {
                  const isEditing = editingCommentId === comment.id;
                  const isReplying = replyingCommentId === comment.id;
                  return (
                    <div key={comment.id} className="flex gap-2 ">
                      {/* Each comment */}

                      {/* Profile image */}
                      <div
                        className="w-7 h-7 min-w-[1.75rem] rounded-full text-lg font-light text-white flex items-center justify-center uppercase"
                        style={{
                          backgroundColor:
                            comment.user?.bgColor || 'cl_primary_background'
                        }}
                      >
                        {/* Profile image */}
                        {/* Icon */}
                        {/* {comment?.user?.avatarUrl && (
                          <>
                            <img
                              className="w-7 h-7 rounded-full mx-auto bg-slate-300"
                              src={comment?.user?.avatarUrl}
                              alt=""
                            />
                          </>
                        )} */}

                        {/* Display name */}
                        {comment?.user?.displayName && (
                          <>{comment.user?.displayName?.charAt(0)}</>
                        )}

                        {/* Email */}
                        {!comment?.user?.displayName && comment.user?.email && (
                          <>{comment.user?.email?.charAt(0)}</>
                        )}
                      </div>

                      {/* Comment block */}
                      <div className=" flex-1 flex flex-col gap-2 min-w-0">
                        <div className="group/comment">
                          {/* Header */}
                          <div className="flex justify-between min-h-[22px]">
                            {/* User info */}
                            <div className="flex">
                              <div className="font-normal">
                                {comment.user?.displayName}
                              </div>
                              <div className="hidden md:flex items-center pl-2 text-xs text-gray-600">
                                {convertISOToLocalDateString(
                                  comment?.created_at || ''
                                )}
                              </div>
                            </div>

                            {/* Delete button */}
                            <div className="flex items-center gap-2">
                              {userData?.id === comment?.user?.id &&
                                !isEditing && (
                                  <>
                                    <CLTooltip content="Edit">
                                      <CLCustomButton
                                        className="hidden group-hover/comment:block p-1 rounded-full hover:bg-slate-100"
                                        onClick={() => {
                                          onClickEdit(comment);
                                        }}
                                      >
                                        {/* SVG icon */}
                                        <svg
                                          width="14"
                                          height="14"
                                          viewBox="0 0 18 18"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M15.8805 5.10904C16.277 4.71261 16.4998 4.17489 16.4999 3.61418C16.4999 3.05347 16.2773 2.5157 15.8808 2.11917C15.4844 1.72263 14.9467 1.49983 14.386 1.49976C13.8253 1.49969 13.2875 1.72236 12.891 2.11879L2.88145 12.1305C2.70732 12.3042 2.57854 12.5179 2.50645 12.753L1.5157 16.017C1.49632 16.0819 1.49485 16.1508 1.51146 16.2164C1.52807 16.2821 1.56214 16.342 1.61005 16.3898C1.65796 16.4376 1.71792 16.4716 1.78357 16.4881C1.84922 16.5046 1.91812 16.503 1.98295 16.4835L5.2477 15.4935C5.48258 15.4221 5.69633 15.2941 5.8702 15.1208L15.8805 5.10904Z"
                                            stroke="#444444"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                          <path
                                            d="M11.25 3.75L14.25 6.75"
                                            stroke="#444444"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </CLCustomButton>
                                    </CLTooltip>
                                    <CLTooltip content="Delete">
                                      <CLCustomButton
                                        className="hidden group-hover/comment:block p-1 rounded-full hover:bg-slate-100"
                                        onClick={() => {
                                          deleteComment(comment);
                                        }}
                                      >
                                        {/* SVG icon */}
                                        <svg
                                          width="14"
                                          height="14"
                                          viewBox="0 0 16 16"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M15.0458 2.72184H10.8458V1.92918C10.8458 1.0921 10.1649 0.411185 9.32785 0.411185L6.67705 0.411133C5.83997 0.411133 5.15918 1.09212 5.15918 1.92913V2.72179L0.958912 2.72184C0.679592 2.72184 0.453125 2.94829 0.453125 3.22762C0.453125 3.50696 0.679578 3.73341 0.958912 3.73341H2.21246V12.2375C2.21246 14.0854 3.71579 15.5886 5.56353 15.5886H10.4411C12.2887 15.5886 13.7922 14.0853 13.7922 12.2375V3.73341H15.0457C15.3251 3.73341 15.5515 3.50696 15.5515 3.22762C15.5517 2.94825 15.3252 2.72184 15.0458 2.72184ZM6.17065 1.92918C6.17065 1.65001 6.39773 1.42288 6.67695 1.42288H9.32789C9.60706 1.42288 9.83419 1.64996 9.83419 1.92918V2.72184H6.17059L6.17065 1.92918ZM12.7808 12.2376C12.7808 13.5275 11.7313 14.5771 10.4413 14.5771H5.56358C4.27363 14.5771 3.22411 13.5276 3.22411 12.2376V3.73345H12.7805L12.7808 12.2376ZM9.22505 11.3746V6.61962C9.22505 6.3403 9.4515 6.11384 9.73083 6.11384C10.0102 6.11384 10.2366 6.34029 10.2366 6.61962V11.3746C10.2366 11.6539 10.0102 11.8803 9.73083 11.8803C9.45146 11.8806 9.22505 11.6539 9.22505 11.3746ZM5.76838 11.3746V6.61962C5.76838 6.3403 5.99483 6.11384 6.27417 6.11384C6.5535 6.11384 6.77995 6.34029 6.77995 6.61962V11.3746C6.77995 11.6539 6.5535 11.8803 6.27417 11.8803C5.99485 11.8806 5.76838 11.6539 5.76838 11.3746Z"
                                            fill="#444444"
                                          />
                                        </svg>
                                      </CLCustomButton>
                                    </CLTooltip>
                                  </>
                                )}
                            </div>
                          </div>

                          <div className="rounded-lg bg-[#f1f5f9] pl-3 p-2 mt-2">
                            {/* Text */}
                            {!isEditing ? (
                              <>
                                <div className="flex flex-row">
                                  {/* 1) Comment text */}
                                  <div className="font-normal w-full">
                                    {renderCommentText(comment.text)}
                                  </div>

                                  {/* 2) Show “Reply” button only if no attachments and not already replying */}
                                  {comment?.run_checklist_comment_files
                                    ?.length === 0 &&
                                    !isReplying && (
                                      <div className="hidden group-hover/comment:flex items-center justify-end">
                                        <CLCustomButton
                                          className="px-2.5 text-[13px] rounded-full border border-gray-700 hover:bg-slate-100"
                                          onClick={() =>
                                            onClickReply(comment.id!)
                                          }
                                        >
                                          Reply
                                        </CLCustomButton>
                                      </div>
                                    )}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex flex-row gap-2 mt-2 w-full">
                                  {/* Text-editing area */}
                                  <CLPrimaryCustomTextarea
                                    ref={textareaRef}
                                    style={{
                                      outline: 'none',
                                      boxShadow: 'none',
                                      borderRadius: '5px',
                                      lineHeight: '1',
                                      padding: '0.5rem',
                                      fontSize: '14px'
                                    }}
                                    className="w-full max-h-28 resize-none bg-transparent text-black caret-black"
                                    placeholder="Edit comment... (Use @ to mention tasks)"
                                    value={previewEditText}
                                    rows={1}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLTextAreaElement>
                                    ) => setPreviewEditText(e.target.value)}
                                  />

                                  {/* Buttons: cross (cancel) & checkmark (save) */}
                                  <div className="flex justify-center items-center gap-2">
                                    {/* Cross / Cancel */}
                                    <CLCustomButton
                                      className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                      onClick={onClickCancel}
                                      disabled={updating}
                                    >
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M18 6L6 18"
                                          stroke="#444444"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M6 6L18 18"
                                          stroke="#444444"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </CLCustomButton>

                                    {/* Checkmark / Save */}
                                    <CLCustomButton
                                      className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                      onClick={() => onClickSave(comment)}
                                      disabled={
                                        updating ||
                                        previewEditText.trim().length === 0
                                      }
                                    >
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M20 6L9 17L4 12"
                                          stroke="#444444"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </CLCustomButton>
                                  </div>
                                </div>
                              </>
                            )}

                            {/* Attachments */}
                            {comment?.run_checklist_comment_files &&
                              comment?.run_checklist_comment_files?.length >
                                0 && (
                                <div className="flex flex-col">
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 w-full py-2.5 pt-3">
                                    {comment.run_checklist_comment_files.map(
                                      (attachmentFile, index) => {
                                        const mimeType =
                                          attachmentFile.org_file?.file
                                            ?.mimeType ?? '';
                                        const isImage =
                                          mimeType.startsWith('image/');

                                        return (
                                          <div key={index}>
                                            {/* File data */}
                                            <div className="group flex justify-between items-center gap-2 border border-black/10 rounded-lg">
                                              <div
                                                className="flex gap-2 items-center overflow-hidden w-full bg-white rounded-lg cursor-pointer"
                                                onClick={() =>
                                                  setActiveAttachment(
                                                    attachmentFile
                                                  )
                                                }
                                              >
                                                {/* Icon */}
                                                <div
                                                  className={`flex items-center justify-center min-w-[44px] w-24 h-16 rounded-l-lg ${
                                                    isImage
                                                      ? 'bg-[#ffb9ff]'
                                                      : 'bg-[#c8f7c5]'
                                                  }`}
                                                >
                                                  {isImage ? (
                                                    // Image SVG
                                                    <img
                                                      src={attachmentFile.url}
                                                      alt={
                                                        attachmentFile?.org_file
                                                          ?.file?.name
                                                      }
                                                      className="w-32 h-16 object-cover rounded-l-lg"
                                                    />
                                                  ) : (
                                                    // Default File SVG
                                                    <svg
                                                      width="36"
                                                      height="36"
                                                      viewBox="0 0 18 18"
                                                      fill="none"
                                                      xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                      <path
                                                        d="M10.502 1.5V4.5C10.502 4.89782 10.66 5.27936 10.9413 5.56066C11.2226 5.84196 11.6041 6 12.002 6H15.002M7.50195 6.75H6.00195M12.002 9.75H6.00195M12.002 12.75H6.00195M11.252 1.5H4.50195C4.10413 1.5 3.7226 1.65804 3.44129 1.93934C3.15999 2.22064 3.00195 2.60218 3.00195 3V15C3.00195 15.3978 3.15999 15.7794 3.44129 16.0607C3.7226 16.342 4.10413 16.5 4.50195 16.5H13.502C13.8998 16.5 14.2813 16.342 14.5626 16.0607C14.8439 15.7794 15.002 15.3978 15.002 15V5.25L11.252 1.5Z"
                                                        stroke="black"
                                                        strokeOpacity="0.6"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                      />
                                                    </svg>
                                                  )}
                                                </div>

                                                {/* Details */}
                                                <div className="overflow-hidden flex flex-row justify-between w-full py-2 gap-2">
                                                  <div
                                                    className="truncate line-clamp-1"
                                                    title={
                                                      attachmentFile?.org_file
                                                        ?.file?.name
                                                    }
                                                  >
                                                    {
                                                      attachmentFile?.org_file
                                                        ?.file?.name
                                                    }
                                                  </div>
                                                  <div className="flex items-center gap-2">
                                                    {/* Download */}
                                                    <div className="pr-2.5">
                                                      {
                                                        <CLTooltip
                                                          content="Download"
                                                          position="left"
                                                        >
                                                          <button
                                                            className=" bg-[#ffa726] p-1 rounded-full"
                                                            onClick={e => {
                                                              e.stopPropagation();
                                                              downloadImage(
                                                                attachmentFile.url as string,
                                                                attachmentFile
                                                                  ?.org_file
                                                                  ?.file?.name
                                                              );
                                                            }}
                                                          >
                                                            <svg
                                                              width="18"
                                                              height="18"
                                                              viewBox="0 0 12 12"
                                                              fill="none"
                                                              xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                              <path
                                                                d="M6.00195 8.5V1.5"
                                                                stroke="black"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                              />
                                                              <path
                                                                d="M3.00195 5.5L6.00195 8.5L9.00195 5.5"
                                                                stroke="black"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                              />
                                                              <path
                                                                d="M9.50195 10.5H2.50195"
                                                                stroke="black"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                              />
                                                            </svg>
                                                          </button>
                                                        </CLTooltip>
                                                      }
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                  {/* bottom‐right “Reply” button */}
                                  <div className="invisible group-hover/comment:visible flex items-end justify-end">
                                    <CLCustomButton
                                      className="px-2.5 text-[13px] rounded-full border border-gray-700 hover:bg-slate-100"
                                      onClick={() => onClickReply(comment.id!)}
                                    >
                                      Reply
                                    </CLCustomButton>
                                  </div>{' '}
                                </div>
                              )}
                          </div>

                          {/* Attachment Preview Pop Up */}
                          {activeAttachment && (
                            <Transition
                              appear
                              show={!!activeAttachment}
                              as={Fragment}
                            >
                              <Dialog
                                as="div"
                                className="relative z-50"
                                onClose={() => setActiveAttachment(null)}
                              >
                                {/* Overlay */}
                                <Transition.Child
                                  as={Fragment}
                                  enter="ease-out duration-300"
                                  enterFrom="opacity-0"
                                  enterTo="opacity-100"
                                  leave="ease-in duration-200"
                                  leaveFrom="opacity-100"
                                  leaveTo="opacity-0"
                                >
                                  <div className="fixed inset-0 bg-black bg-opacity-40" />
                                </Transition.Child>

                                {/* Centered Modal Panel */}
                                <div className="fixed inset-0 overflow-y-auto">
                                  <div className="flex min-h-full items-center justify-center p-4 text-center">
                                    <Transition.Child
                                      as={Fragment}
                                      enter="ease-out duration-300"
                                      enterFrom="opacity-0 scale-95"
                                      enterTo="opacity-100 scale-100"
                                      leave="ease-in duration-200"
                                      leaveFrom="opacity-100 scale-100"
                                      leaveTo="opacity-0 scale-95"
                                    >
                                      <Dialog.Panel
                                        className="w-full max-w-5xl transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all max-h-[90vh]"
                                        onClick={e => e.stopPropagation()}
                                      >
                                        {/* Header */}
                                        <div className="flex justify-between items-center p-4 border-b border-gray-200">
                                          <div className="text-sm font-medium text-gray-800 line-clamp-1 overflow-hidden mr-4">
                                            {
                                              activeAttachment.org_file?.file
                                                ?.name
                                            }
                                          </div>
                                          <div className="flex items-center gap-2">
                                            {/* Download button */}
                                            <CLTooltip
                                              content="Download"
                                              position="bottom"
                                            >
                                              <CLCustomButton
                                                className="flex justify-center items-center rounded-full p-1 shadow-lg hover:bg-black/10"
                                                onClick={() =>
                                                  downloadImage(
                                                    activeAttachment.url as string,
                                                    activeAttachment.org_file
                                                      ?.file?.name ??
                                                      'default-filename'
                                                  )
                                                }
                                              >
                                                {/* download icon */}
                                                <svg
                                                  width="16"
                                                  height="16"
                                                  viewBox="0 0 12 12"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <path
                                                    d="M6.00195 8.5V1.5"
                                                    stroke="black"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  />
                                                  <path
                                                    d="M3.00195 5.5L6.00195 8.5L9.00195 5.5"
                                                    stroke="black"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  />
                                                  <path
                                                    d="M9.50195 10.5H2.50195"
                                                    stroke="black"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  />
                                                </svg>
                                              </CLCustomButton>
                                            </CLTooltip>

                                            {/* Close button */}
                                            <button
                                              className="p-1.5 rounded-full hover:bg-gray-100"
                                              onClick={() =>
                                                setActiveAttachment(null)
                                              }
                                            >
                                              <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="text-gray-700"
                                              >
                                                <path
                                                  d="M2 2L14 14M14 2L2 14"
                                                  stroke="currentColor"
                                                  strokeWidth="2"
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                />
                                              </svg>
                                            </button>
                                          </div>
                                        </div>

                                        {/* Body */}
                                        <div className="flex-1 p-4 flex justify-center items-center overflow-auto max-h-[80vh]">
                                          {activeAttachment.org_file?.file?.mimeType?.startsWith(
                                            'image/'
                                          ) ? (
                                            <img
                                              src={activeAttachment.url}
                                              alt={
                                                activeAttachment.org_file.file
                                                  .name
                                              }
                                              className="max-w-full max-h-[68vh] min-h-[40vh] object-contain"
                                            />
                                          ) : activeAttachment.org_file?.file
                                              ?.mimeType ===
                                            'application/pdf' ? (
                                            <iframe
                                              src={activeAttachment.url}
                                              className="w-full h-full min-h-[68vh]"
                                              title={
                                                activeAttachment.org_file.file
                                                  .name
                                              }
                                            />
                                          ) : ['.docx', '.pptx'].some(ext =>
                                              activeAttachment.org_file?.file?.name
                                                ?.toLowerCase()
                                                .endsWith(ext)
                                            ) ? (
                                            <iframe
                                              src={`https://docs.google.com/gview?url=${encodeURIComponent(
                                                activeAttachment.url as string
                                              )}&embedded=true`}
                                              className="w-full h-full min-h-[68vh]"
                                              title={
                                                activeAttachment.org_file?.file
                                                  ?.name
                                              }
                                            />
                                          ) : textPreview !== null ? (
                                            activeAttachment.org_file?.file
                                              ?.mimeType === 'text/csv' ? (
                                              <div className="overflow-auto max-h-[68vh]">
                                                <table className="w-full table-auto border-collapse">
                                                  <tbody>
                                                    {textPreview
                                                      .split('\n')
                                                      .map((line, i) => (
                                                        <tr
                                                          key={i}
                                                          className={
                                                            i === 0
                                                              ? 'font-bold'
                                                              : ''
                                                          }
                                                        >
                                                          {line
                                                            .split(',')
                                                            .map((cell, j) => (
                                                              <td
                                                                key={j}
                                                                className="border px-2 py-1 text-xs whitespace-nowrap"
                                                              >
                                                                {cell}
                                                              </td>
                                                            ))}
                                                        </tr>
                                                      ))}
                                                  </tbody>
                                                </table>
                                              </div>
                                            ) : (
                                              <pre className="overflow-auto max-h-[68vh] whitespace-pre-wrap text-sm">
                                                {textPreview}
                                              </pre>
                                            )
                                          ) : (
                                            <div className="text-gray-500">
                                              No preview available
                                            </div>
                                          )}
                                        </div>
                                      </Dialog.Panel>
                                    </Transition.Child>
                                  </div>
                                </div>
                              </Dialog>
                            </Transition>
                          )}
                        </div>

                        {/* 3) When “Reply” is active, render a textarea + cross/tick below */}
                        {isReplying && (
                          <div className=" ml-6 flex flex-row gap-2 mt-2 w-[97%]">
                            {/* Profile image */}
                            <div className="flex justify-center items-center">
                              <div
                                className="w-7 h-7 min-w-[1.75rem] rounded-full text-lg text-white flex items-center justify-center uppercase"
                                style={{
                                  backgroundColor:
                                    runChecklistComments?.find(
                                      c =>
                                        c.user?.id === userData?.id &&
                                        c.user?.bgColor
                                    )?.user?.bgColor ??
                                    runTaskComments?.find(
                                      c =>
                                        c.user?.id === userData?.id &&
                                        c.user?.bgColor
                                    )?.user?.bgColor ??
                                    runChecklistReplyComments?.find(
                                      c =>
                                        c.user?.id === userData?.id &&
                                        c.user?.bgColor
                                    )?.user?.bgColor ??
                                    runChecklistTaskReplyComments?.find(
                                      c =>
                                        c.user?.id === userData?.id &&
                                        c.user?.bgColor
                                    )?.user?.bgColor ??
                                    CommonConstants.DEFAULT_COLORS[0]
                                }}
                              >
                                {/* Profile image */}
                                {/* Icon */}
                                {/* {comment?.user?.avatarUrl && (
                                    <>
                                      <img
                                        className="w-7 h-7 rounded-full mx-auto bg-slate-300"
                                        src={comment?.user?.avatarUrl}
                                        alt=""
                                      />
                                    </>
                                  )} */}

                                {/* Display name */}
                                {userData?.displayName && (
                                  <>{userData?.displayName?.charAt(0)}</>
                                )}

                                {/* Email */}
                                {!userData?.displayName && userData?.email && (
                                  <>{userData?.email?.charAt(0)}</>
                                )}
                              </div>
                            </div>
                            <CLPrimaryCustomTextarea
                              ref={replyTextareaRef}
                              style={{
                                outline: 'none',
                                boxShadow: 'none',
                                borderRadius: '5px',
                                lineHeight: '1',
                                padding: '0.5rem',
                                fontSize: '14px'
                              }}
                              className="w-full max-h-28 resize-none bg-transparent text-black caret-black"
                              placeholder="Write a reply…"
                              value={previewReplyText}
                              rows={1}
                              onChange={(
                                e: React.ChangeEvent<HTMLTextAreaElement>
                              ) => setPreviewReplyText(e.target.value)}
                            />

                            {/* Cross (cancel) */}
                            <div className="flex justify-center items-center gap-2">
                              <CLCustomButton
                                className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                onClick={onClickCancelReply}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M18 6L6 18"
                                    stroke="#444444"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M6 6L18 18"
                                    stroke="#444444"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </CLCustomButton>

                              {/* Checkmark (send/save reply) */}
                              <CLCustomButton
                                className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                onClick={() => {
                                  // call your “save reply” function here, e.g.:
                                  saveReply(comment.id as string);
                                  setReplyingCommentId(null);
                                  setPreviewReplyText('');
                                }}
                                disabled={previewReplyText.trim().length === 0}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M20 6L9 17L4 12"
                                    stroke="#444444"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </CLCustomButton>
                            </div>
                          </div>
                        )}

                        {/* ---- REPLIES: find all replies whose reply_id === comment.id ---- */}
                        {runChecklistReplyComments &&
                          runChecklistReplyComments?.length > 0 &&
                          runChecklistReplyComments
                            .filter(r => r.reply_id === comment.id)
                            .map(reply => {
                              const isEditing = editingCommentId === reply.id;
                              const isReplying = replyingCommentId === reply.id;
                              return (
                                <div className="flex flex-col" key={reply.id}>
                                  <div className="ml-4 mt-2 group/reply flex flex-row gap-2 rounded-md p-2 pr-0">
                                    {/* Profile image */}
                                    <div
                                      className="w-7 h-7 min-w-[1.75rem] rounded-full text-lg font-light text-white flex items-center justify-center uppercase"
                                      style={{
                                        backgroundColor:
                                          reply.user?.bgColor ||
                                          'cl_primary_background'
                                      }}
                                    >
                                      {/* Profile image */}
                                      {/* Icon */}
                                      {/* {comment?.user?.avatarUrl && (
                                    <>
                                      <img
                                        className="w-7 h-7 rounded-full mx-auto bg-slate-300"
                                        src={comment?.user?.avatarUrl}
                                        alt=""
                                      />
                                    </>
                                  )} */}
                                      {/* Display name */}
                                      {reply?.user?.displayName && (
                                        <>
                                          {reply.user?.displayName?.charAt(0)}
                                        </>
                                      )}

                                      {/* Email */}
                                      {!reply?.user?.displayName &&
                                        reply.user?.email && (
                                          <>{reply.user?.email?.charAt(0)}</>
                                        )}
                                    </div>

                                    {/* Reply block */}
                                    <div className="flex-1 flex flex-col gap-2">
                                      {/* Header */}
                                      <div className="flex justify-between min-h-[22px]">
                                        {/* User info */}
                                        <div className="flex">
                                          <div className="font-normal">
                                            {reply.user?.displayName}
                                          </div>
                                          <div className="hidden md:flex items-center pl-2 text-xs text-gray-600">
                                            {convertISOToLocalDateString(
                                              reply?.created_at || ''
                                            )}
                                          </div>
                                        </div>

                                        {/* Delete button */}
                                        <div className="flex items-center gap-2">
                                          {userData?.id === reply?.user?.id &&
                                            !isEditing && (
                                              <>
                                                <CLTooltip content="Edit">
                                                  <CLCustomButton
                                                    className="hidden group-hover/reply:block p-1 rounded-full hover:bg-slate-100"
                                                    onClick={() => {
                                                      onClickEdit(reply);
                                                    }}
                                                  >
                                                    {/* SVG icon */}
                                                    <svg
                                                      width="14"
                                                      height="14"
                                                      viewBox="0 0 18 18"
                                                      fill="none"
                                                      xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                      <path
                                                        d="M15.8805 5.10904C16.277 4.71261 16.4998 4.17489 16.4999 3.61418C16.4999 3.05347 16.2773 2.5157 15.8808 2.11917C15.4844 1.72263 14.9467 1.49983 14.386 1.49976C13.8253 1.49969 13.2875 1.72236 12.891 2.11879L2.88145 12.1305C2.70732 12.3042 2.57854 12.5179 2.50645 12.753L1.5157 16.017C1.49632 16.0819 1.49485 16.1508 1.51146 16.2164C1.52807 16.2821 1.56214 16.342 1.61005 16.3898C1.65796 16.4376 1.71792 16.4716 1.78357 16.4881C1.84922 16.5046 1.91812 16.503 1.98295 16.4835L5.2477 15.4935C5.48258 15.4221 5.69633 15.2941 5.8702 15.1208L15.8805 5.10904Z"
                                                        stroke="#444444"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                      />
                                                      <path
                                                        d="M11.25 3.75L14.25 6.75"
                                                        stroke="#444444"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                      />
                                                    </svg>
                                                  </CLCustomButton>
                                                </CLTooltip>
                                                <CLTooltip content="Delete">
                                                  <CLCustomButton
                                                    className="hidden group-hover/reply:block p-1 rounded-full hover:bg-slate-100"
                                                    onClick={() => {
                                                      deleteComment(reply);
                                                    }}
                                                  >
                                                    {/* SVG icon */}
                                                    <svg
                                                      width="14"
                                                      height="14"
                                                      viewBox="0 0 16 16"
                                                      fill="none"
                                                      xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                      <path
                                                        d="M15.0458 2.72184H10.8458V1.92918C10.8458 1.0921 10.1649 0.411185 9.32785 0.411185L6.67705 0.411133C5.83997 0.411133 5.15918 1.09212 5.15918 1.92913V2.72179L0.958912 2.72184C0.679592 2.72184 0.453125 2.94829 0.453125 3.22762C0.453125 3.50696 0.679578 3.73341 0.958912 3.73341H2.21246V12.2375C2.21246 14.0854 3.71579 15.5886 5.56353 15.5886H10.4411C12.2887 15.5886 13.7922 14.0853 13.7922 12.2375V3.73341H15.0457C15.3251 3.73341 15.5515 3.50696 15.5515 3.22762C15.5517 2.94825 15.3252 2.72184 15.0458 2.72184ZM6.17065 1.92918C6.17065 1.65001 6.39773 1.42288 6.67695 1.42288H9.32789C9.60706 1.42288 9.83419 1.64996 9.83419 1.92918V2.72184H6.17059L6.17065 1.92918ZM12.7808 12.2376C12.7808 13.5275 11.7313 14.5771 10.4413 14.5771H5.56358C4.27363 14.5771 3.22411 13.5276 3.22411 12.2376V3.73345H12.7805L12.7808 12.2376ZM9.22505 11.3746V6.61962C9.22505 6.3403 9.4515 6.11384 9.73083 6.11384C10.0102 6.11384 10.2366 6.34029 10.2366 6.61962V11.3746C10.2366 11.6539 10.0102 11.8803 9.73083 11.8803C9.45146 11.8806 9.22505 11.6539 9.22505 11.3746ZM5.76838 11.3746V6.61962C5.76838 6.3403 5.99483 6.11384 6.27417 6.11384C6.5535 6.11384 6.77995 6.34029 6.77995 6.61962V11.3746C6.77995 11.6539 6.5535 11.8803 6.27417 11.8803C5.99485 11.8806 5.76838 11.6539 5.76838 11.3746Z"
                                                        fill="#444444"
                                                      />
                                                    </svg>
                                                  </CLCustomButton>
                                                </CLTooltip>
                                              </>
                                            )}
                                        </div>
                                      </div>

                                      {/* Text */}
                                      {!isEditing ? (
                                        <>
                                          <div className="flex flex-row mt-2 rounded-md p-2.5 cl_primary_background">
                                            {/* 1) Comment text */}
                                            <div className="font-normal w-full">
                                              {reply.text}
                                            </div>

                                            {/* 2) Show “Reply” button only if no attachments and not already replying */}
                                            {reply?.run_checklist_comment_files
                                              ?.length === 0 &&
                                              !isReplying && (
                                                <div className="hidden group-hover/reply:flex items-center justify-end">
                                                  <CLCustomButton
                                                    className="px-2.5 text-[13px] rounded-full border border-gray-700 hover:bg-slate-100"
                                                    onClick={() =>
                                                      onClickReply(reply.id!)
                                                    }
                                                  >
                                                    Reply
                                                  </CLCustomButton>
                                                </div>
                                              )}
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <div className="flex flex-row gap-2 w-full">
                                            {/* Text-editing area */}
                                            <CLPrimaryCustomTextarea
                                              ref={textareaRef}
                                              style={{
                                                outline: 'none',
                                                boxShadow: 'none',
                                                borderRadius: '5px',
                                                lineHeight: '1',
                                                padding: '0.5rem',
                                                fontSize: '14px'
                                              }}
                                              className="w-full max-h-28 resize-none bg-transparent text-black caret-black"
                                              placeholder="Edit comment... (Use @ to mention tasks)"
                                              value={previewEditText}
                                              rows={1}
                                              onChange={(
                                                e: React.ChangeEvent<HTMLTextAreaElement>
                                              ) =>
                                                setPreviewEditText(
                                                  e.target.value
                                                )
                                              }
                                            />

                                            {/* Buttons: cross (cancel) & checkmark (save) */}
                                            <div className="flex justify-center items-center gap-2">
                                              {/* Cross / Cancel */}
                                              <CLCustomButton
                                                className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                                onClick={onClickCancel}
                                                disabled={updating}
                                              >
                                                <svg
                                                  width="16"
                                                  height="16"
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <path
                                                    d="M18 6L6 18"
                                                    stroke="#444444"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  />
                                                  <path
                                                    d="M6 6L18 18"
                                                    stroke="#444444"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  />
                                                </svg>
                                              </CLCustomButton>

                                              {/* Checkmark / Save */}
                                              <CLCustomButton
                                                className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                                onClick={() =>
                                                  onClickSave(reply)
                                                }
                                                disabled={
                                                  updating ||
                                                  previewEditText.trim()
                                                    .length === 0
                                                }
                                              >
                                                <svg
                                                  width="16"
                                                  height="16"
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <path
                                                    d="M20 6L9 17L4 12"
                                                    stroke="#444444"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  />
                                                </svg>
                                              </CLCustomButton>
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {/* 3) When “Reply” is active, render a textarea + cross/tick below */}
                                  {isReplying && (
                                    <div className=" ml-[86px] mb-2 flex flex-row gap-2 mt-2 w-[88%]">
                                      {/* Profile image */}
                                      <div className="flex justify-center items-center">
                                        <div
                                          className="w-7 h-7 min-w-[1.75rem] rounded-full text-lg text-white flex items-center justify-center uppercase"
                                          style={{
                                            backgroundColor:
                                              runChecklistComments?.find(
                                                c =>
                                                  c.user?.id === userData?.id &&
                                                  c.user?.bgColor
                                              )?.user?.bgColor ??
                                              runTaskComments?.find(
                                                c =>
                                                  c.user?.id === userData?.id &&
                                                  c.user?.bgColor
                                              )?.user?.bgColor ??
                                              runChecklistReplyComments?.find(
                                                c =>
                                                  c.user?.id === userData?.id &&
                                                  c.user?.bgColor
                                              )?.user?.bgColor ??
                                              runChecklistTaskReplyComments?.find(
                                                c =>
                                                  c.user?.id === userData?.id &&
                                                  c.user?.bgColor
                                              )?.user?.bgColor ??
                                              CommonConstants.DEFAULT_COLORS[0]
                                          }}
                                        >
                                          {/* Profile image */}
                                          {/* Icon */}
                                          {/* {comment?.user?.avatarUrl && (
                                        <>
                                          <img
                                            className="w-7 h-7 rounded-full mx-auto bg-slate-300"
                                            src={comment?.user?.avatarUrl}
                                            alt=""
                                          />
                                        </>
                                      )} */}

                                          {/* Display name */}
                                          {userData?.displayName && (
                                            <>
                                              {userData?.displayName?.charAt(0)}
                                            </>
                                          )}

                                          {/* Email */}
                                          {!userData?.displayName &&
                                            userData?.email && (
                                              <>{userData?.email?.charAt(0)}</>
                                            )}
                                        </div>
                                      </div>
                                      <CLPrimaryCustomTextarea
                                        ref={replyTextareaRef}
                                        style={{
                                          outline: 'none',
                                          boxShadow: 'none',
                                          borderRadius: '5px',
                                          lineHeight: '1',
                                          padding: '0.5rem',
                                          fontSize: '14px'
                                        }}
                                        className="w-full max-h-28 resize-none bg-transparent text-black caret-black"
                                        placeholder="Write a reply…"
                                        value={previewReplyText}
                                        rows={1}
                                        onChange={(
                                          e: React.ChangeEvent<HTMLTextAreaElement>
                                        ) =>
                                          setPreviewReplyText(e.target.value)
                                        }
                                      />

                                      {/* Cross (cancel) */}
                                      <div className="flex justify-center items-center gap-2">
                                        <CLCustomButton
                                          className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                          onClick={onClickCancelReply}
                                        >
                                          <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M18 6L6 18"
                                              stroke="#444444"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                            <path
                                              d="M6 6L18 18"
                                              stroke="#444444"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        </CLCustomButton>

                                        {/* Checkmark (send/save reply) */}
                                        <CLCustomButton
                                          className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                          onClick={() => {
                                            // call your “save reply” function here, e.g.:
                                            saveReply(reply.id as string);
                                            setReplyingCommentId(null);
                                            setPreviewReplyText('');
                                          }}
                                          disabled={
                                            previewReplyText.trim().length === 0
                                          }
                                        >
                                          <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M20 6L9 17L4 12"
                                              stroke="#444444"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        </CLCustomButton>
                                      </div>
                                    </div>
                                  )}

                                  {/* ---- REPLY TO REPLIES: find all replies whose reply_id === comment.id ---- */}
                                  {runChecklistReplyToReplyComments &&
                                    runChecklistReplyToReplyComments?.length >
                                      0 &&
                                    runChecklistReplyToReplyComments
                                      .filter(r => r.reply_id === reply.id)
                                      .map(replyToReply => {
                                        const isEditing =
                                          editingCommentId === replyToReply.id;
                                        return (
                                          <div
                                            key={replyToReply.id}
                                            className="ml-[76px] mt-2 group/reply flex flex-row gap-2 rounded-md p-2 pr-0"
                                          >
                                            {/* Profile image */}
                                            <div
                                              className="w-7 h-7 min-w-[1.75rem] rounded-full text-lg font-light text-white flex items-center justify-center uppercase"
                                              style={{
                                                backgroundColor:
                                                  replyToReply.user?.bgColor ||
                                                  'cl_primary_background'
                                              }}
                                            >
                                              {/* Profile image */}
                                              {/* Icon */}
                                              {/* {comment?.user?.avatarUrl && (
                                    <>
                                      <img
                                        className="w-7 h-7 rounded-full mx-auto bg-slate-300"
                                        src={comment?.user?.avatarUrl}
                                        alt=""
                                      />
                                    </>
                                  )} */}
                                              {/* Display name */}
                                              {replyToReply?.user
                                                ?.displayName && (
                                                <>
                                                  {replyToReply.user?.displayName?.charAt(
                                                    0
                                                  )}
                                                </>
                                              )}

                                              {/* Email */}
                                              {!replyToReply?.user
                                                ?.displayName &&
                                                replyToReply.user?.email && (
                                                  <>
                                                    {replyToReply.user?.email?.charAt(
                                                      0
                                                    )}
                                                  </>
                                                )}
                                            </div>

                                            {/* Reply block */}
                                            <div className="flex-1 flex flex-col gap-2">
                                              {/* Header */}
                                              <div className="flex justify-between min-h-[22px]">
                                                {/* User info */}
                                                <div className="flex">
                                                  <div className="font-normal">
                                                    {
                                                      replyToReply.user
                                                        ?.displayName
                                                    }
                                                  </div>
                                                  <div className="hidden md:flex items-center pl-2 text-xs text-gray-600">
                                                    {convertISOToLocalDateString(
                                                      replyToReply?.created_at ||
                                                        ''
                                                    )}
                                                  </div>
                                                </div>

                                                {/* Delete button */}
                                                <div className="flex items-center gap-2">
                                                  {userData?.id ===
                                                    replyToReply?.user?.id &&
                                                    !isEditing && (
                                                      <>
                                                        <CLTooltip content="Edit">
                                                          <CLCustomButton
                                                            className="hidden group-hover/reply:block p-1 rounded-full hover:bg-slate-100"
                                                            onClick={() => {
                                                              onClickEdit(
                                                                replyToReply
                                                              );
                                                            }}
                                                          >
                                                            {/* SVG icon */}
                                                            <svg
                                                              width="14"
                                                              height="14"
                                                              viewBox="0 0 18 18"
                                                              fill="none"
                                                              xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                              <path
                                                                d="M15.8805 5.10904C16.277 4.71261 16.4998 4.17489 16.4999 3.61418C16.4999 3.05347 16.2773 2.5157 15.8808 2.11917C15.4844 1.72263 14.9467 1.49983 14.386 1.49976C13.8253 1.49969 13.2875 1.72236 12.891 2.11879L2.88145 12.1305C2.70732 12.3042 2.57854 12.5179 2.50645 12.753L1.5157 16.017C1.49632 16.0819 1.49485 16.1508 1.51146 16.2164C1.52807 16.2821 1.56214 16.342 1.61005 16.3898C1.65796 16.4376 1.71792 16.4716 1.78357 16.4881C1.84922 16.5046 1.91812 16.503 1.98295 16.4835L5.2477 15.4935C5.48258 15.4221 5.69633 15.2941 5.8702 15.1208L15.8805 5.10904Z"
                                                                stroke="#444444"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                              />
                                                              <path
                                                                d="M11.25 3.75L14.25 6.75"
                                                                stroke="#444444"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                              />
                                                            </svg>
                                                          </CLCustomButton>
                                                        </CLTooltip>
                                                        <CLTooltip content="Delete">
                                                          <CLCustomButton
                                                            className="hidden group-hover/reply:block p-1 rounded-full hover:bg-slate-100"
                                                            onClick={() => {
                                                              deleteComment(
                                                                replyToReply
                                                              );
                                                            }}
                                                          >
                                                            {/* SVG icon */}
                                                            <svg
                                                              width="14"
                                                              height="14"
                                                              viewBox="0 0 16 16"
                                                              fill="none"
                                                              xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                              <path
                                                                d="M15.0458 2.72184H10.8458V1.92918C10.8458 1.0921 10.1649 0.411185 9.32785 0.411185L6.67705 0.411133C5.83997 0.411133 5.15918 1.09212 5.15918 1.92913V2.72179L0.958912 2.72184C0.679592 2.72184 0.453125 2.94829 0.453125 3.22762C0.453125 3.50696 0.679578 3.73341 0.958912 3.73341H2.21246V12.2375C2.21246 14.0854 3.71579 15.5886 5.56353 15.5886H10.4411C12.2887 15.5886 13.7922 14.0853 13.7922 12.2375V3.73341H15.0457C15.3251 3.73341 15.5515 3.50696 15.5515 3.22762C15.5517 2.94825 15.3252 2.72184 15.0458 2.72184ZM6.17065 1.92918C6.17065 1.65001 6.39773 1.42288 6.67695 1.42288H9.32789C9.60706 1.42288 9.83419 1.64996 9.83419 1.92918V2.72184H6.17059L6.17065 1.92918ZM12.7808 12.2376C12.7808 13.5275 11.7313 14.5771 10.4413 14.5771H5.56358C4.27363 14.5771 3.22411 13.5276 3.22411 12.2376V3.73345H12.7805L12.7808 12.2376ZM9.22505 11.3746V6.61962C9.22505 6.3403 9.4515 6.11384 9.73083 6.11384C10.0102 6.11384 10.2366 6.34029 10.2366 6.61962V11.3746C10.2366 11.6539 10.0102 11.8803 9.73083 11.8803C9.45146 11.8806 9.22505 11.6539 9.22505 11.3746ZM5.76838 11.3746V6.61962C5.76838 6.3403 5.99483 6.11384 6.27417 6.11384C6.5535 6.11384 6.77995 6.34029 6.77995 6.61962V11.3746C6.77995 11.6539 6.5535 11.8803 6.27417 11.8803C5.99485 11.8806 5.76838 11.6539 5.76838 11.3746Z"
                                                                fill="#444444"
                                                              />
                                                            </svg>
                                                          </CLCustomButton>
                                                        </CLTooltip>
                                                      </>
                                                    )}
                                                </div>
                                              </div>

                                              {/* Text */}
                                              {!isEditing ? (
                                                <>
                                                  <div className="flex flex-row mt-2">
                                                    {/* 1) Comment text */}
                                                    <div className="font-normal w-full rounded-md p-2.5 cl_primary_background">
                                                      {replyToReply.text}
                                                    </div>
                                                  </div>
                                                </>
                                              ) : (
                                                <>
                                                  <div className="flex flex-row gap-2 w-full">
                                                    {/* Text-editing area */}
                                                    <CLPrimaryCustomTextarea
                                                      ref={textareaRef}
                                                      style={{
                                                        outline: 'none',
                                                        boxShadow: 'none',
                                                        borderRadius: '5px',
                                                        lineHeight: '1',
                                                        padding: '0.5rem',
                                                        fontSize: '14px'
                                                      }}
                                                      className="w-full max-h-28 resize-none bg-transparent text-black caret-black"
                                                      placeholder="Edit comment... (Use @ to mention tasks)"
                                                      value={previewEditText}
                                                      rows={1}
                                                      onChange={(
                                                        e: React.ChangeEvent<HTMLTextAreaElement>
                                                      ) =>
                                                        setPreviewEditText(
                                                          e.target.value
                                                        )
                                                      }
                                                    />

                                                    {/* Buttons: cross (cancel) & checkmark (save) */}
                                                    <div className="flex justify-center items-center gap-2">
                                                      {/* Cross / Cancel */}
                                                      <CLCustomButton
                                                        className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                                        onClick={onClickCancel}
                                                        disabled={updating}
                                                      >
                                                        <svg
                                                          width="16"
                                                          height="16"
                                                          viewBox="0 0 24 24"
                                                          fill="none"
                                                          xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                          <path
                                                            d="M18 6L6 18"
                                                            stroke="#444444"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                          />
                                                          <path
                                                            d="M6 6L18 18"
                                                            stroke="#444444"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                          />
                                                        </svg>
                                                      </CLCustomButton>

                                                      {/* Checkmark / Save */}
                                                      <CLCustomButton
                                                        className="p-1 rounded-full hover:bg-slate-100 border border-1 border-gray-700"
                                                        onClick={() =>
                                                          onClickSave(
                                                            replyToReply
                                                          )
                                                        }
                                                        disabled={
                                                          updating ||
                                                          previewEditText.trim()
                                                            .length === 0
                                                        }
                                                      >
                                                        <svg
                                                          width="16"
                                                          height="16"
                                                          viewBox="0 0 24 24"
                                                          fill="none"
                                                          xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                          <path
                                                            d="M20 6L9 17L4 12"
                                                            stroke="#444444"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                          />
                                                        </svg>
                                                      </CLCustomButton>
                                                    </div>
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                </div>
                              );
                            })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
}
